import type { Dictionary } from './types';

export const en: Dictionary = {
  common: {
    appName: 'Builder',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    back: 'Back',
    loading: 'Loading…',
    retry: 'Retry',
    search: 'Search',
    seeAll: 'See all',
    error: 'Error',
    success: 'Success',
    yes: 'Yes',
    no: 'No',
    and: 'and',
    or: 'or',
    notFound: 'Not found',
    somethingWentWrong: 'Something went wrong.',
  },
  undoToast: {
    action: 'Undo',
    dismiss: 'Close notice',
    submitting: 'Undoing…',
    labelWithCounter: (label, secs) => `${label} · ${secs}s`,
    deletedLabel: {
      rdo: 'Daily log deleted',
      budgetItem: 'Budget item deleted',
      fvs: 'Inspection deleted',
    },
  },
  inbox: {
    tabs: {
      approvals: 'Approvals',
      notifications: 'Notifications',
    },
    filters: {
      projectsAria: 'Filter by project',
      kindsAria: 'Filter by type',
      allProjects: 'All projects',
      allKinds: 'All types',
      clearAll: 'Clear filters',
      noMatch: 'No items match this filter',
      noMatchHint: 'Try adjusting project or type, or click "Clear filters".',
      noProject: 'No project',
      kindLabel: {
        proposal_pending: 'Suggestions',
        client_error_open: 'Errors',
        self_heal_failed: 'Unexpected change',
        anomaly_pending: 'Points of attention',
        po_pending: 'PO pending',
        rnc_unassigned: 'Non-conformance',
        fvs_overdue: 'Inspection',
        doc_expiring: 'Documents',
        anomaly_high: 'Points of attention',
        rdo_missing: 'RDO',
        medicao_draft: 'Measurement',
      },
    },
  },
  auth: {
    loginTitle: 'Sign in to Builder',
    loginSubtitle: 'Run your projects with transparency.',
    registerTitle: 'Create your account',
    registerSubtitle: 'Start organizing your projects in minutes.',
    email: 'Email',
    password: 'Password',
    fullName: 'Full name',
    phone: 'Phone',
    phoneOptional: 'Phone (optional)',
    loginCta: 'Sign in',
    registerCta: 'Create account',
    needAccount: 'New here? Create account',
    haveAccount: 'Already have an account? Sign in',
    forgotPassword: 'Forgot password',
    logout: 'Log out',
    badCredentials: 'Wrong email or password.',
    iAmA: 'I am a',
    passwordHint: 'At least 8 characters, with letters and numbers.',
    couldNotCreate: 'Could not create account.',
    continueWithGoogle: 'Continue with Google',
    orContinueWithEmail: 'or sign in with email & password',
    googleSignInFailed: 'Could not sign in with Google.',
  },
  nav: {
    projects: 'Projects',
    myProjects: 'My projects',
    portfolio: 'Portfolio',
    // Wave 351-C-fix · same word in both locales; matches `/inbox`.
    inbox: 'Inbox',
    alertas: 'Alerts',
    anomalias: 'Points of attention',
    approvals: 'Approvals',
    activity: 'Activity',
    trena: 'Trena',
    trenaDescription: 'Smart estimate by m²',
    hojeDescription: 'What to do now — one decision at a time', // W148 · 2026-05-28
    inboxDescription: 'Everything pending — pick what matters', // W148 · 2026-05-28
    alertasDescription: 'Automatic alerts about schedule risk', // W148 · 2026-05-28
    anomaliasDescription: 'Points outside the norm detected by the system', // W148 · 2026-05-28
    userApprovalsDescription: 'Requests waiting for your OK', // W148 · 2026-05-28
    executivo: 'Executive',
    ownerPortal: 'Client Portal',
    userApprovals: 'Approve signups',
    ajuda: 'Help center',
    settings: 'Settings',
    account: 'Account',
    theme: 'Theme',
    language: 'Language',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    sectionOverview: 'Overview',
    sectionPending: 'Pending',
    sectionAccount: 'Account',
    // Wave 352-B · NAV_REGISTRY-driven SideNav.
    relatorios: 'Reports',
    fornecedores: 'Vendors',
    procurementCockpit: 'Modality recommender',
    procurementFrameworks: 'Framework contracts',
    procurementDashboard: 'Procurement dashboard',
    adminAuctions: 'Reverse auctions',
    broker: 'Supplier Auctions',
    // Wave UUUU · cross-project quality hub nav entry.
    qualidadeHub: 'Quality Hub',
    // Wave 353 · legacy desktop project list label.
    projectsList: 'Project list',
    adminHome: 'Admin Panel',
    adminCommandControl: 'Command & Control',
    adminBrain: 'Brain',
    adminSaude: 'Health',
    adminDeploys: 'Deploys',
    adminAutonomia: 'Autonomy',
    adminConectores: 'Connectors',
    adminMetaTemplates: 'Meta Templates',
    adminLabsGenerative: 'Labs · Generative Engineering',
    adminLabsGenerativeHistory: 'Lab History',
    adminUsuarios: 'Users',
    // Wave 390-395 · autonomy sub-routes
    adminAutonomyIncidents: 'Incidents',
    adminAutonomyPMLoop: 'PM Loop',
    adminAutonomyDataSelfHeal: 'Data self-heal',
    adminAutonomyWorkflowSelfHeal: 'Workflow self-heal',
    adminAutonomyProcurementAutopilot: 'Procurement autopilot',
    adminAutonomyResourceOptimizer: 'Resource optimizer',
    adminAutonomyRuleAutopilot: 'Rule autopilot',
    adminAutonomyQualityAutopilot: 'Quality autopilot',
    adminAutonomyClaimsDetection: 'Claims detection',
    adminAutonomyOwnerTrustPlus: 'Owner trust+',
    adminAutonomyAgentGovernor: 'Agent governor',
    adminAutonomySelfTuning: 'Self-tuning',
    adminAutonomyDailyBrief: 'Daily brief',
    adminAutonomySLA: 'SLA monitor',
    adminAutonomyRadarCritic: 'Radar critic',
    adminAutonomyChaos: 'Chaos engine',
    adminAutonomyExceptionsOnly: 'Exceptions only',
    // Financial & Compliance
    adminMonthlyClose: 'Monthly close',
    adminFinancialRisk: 'Financial risk',
    adminAuditPack: 'Audit pack',
    // Observability
    adminMemoryGraph: 'Memory graph',
    adminBenchmarking: 'Benchmarking',
    adminSOP: 'SOPs',
    // Tooling
    adminPolicy: 'Policy as Code',
    adminSimulation: 'Simulation',
    // W383 R8/R9 · product analytics + decision graph + Housy unknowns
    adminProductAnalytics: 'Product analytics',
    adminDecisionGraph: 'Decision graph',
    adminHousyUnknowns: 'Housy unknowns',
    // Tier F · radar deep scoring + house report
    adminRadarDeepScoring: 'Radar · scoring',
    adminRadarHouseReport: 'Radar · report',
    // Tier F · platform admin
    adminTenants: 'Organizations',
    adminBulkOps: 'Bulk operations',
    adminAuditLog: 'Audit log',
    adminObservability: 'Observability',
    adminBackups: 'Backups',
    // Wave SYSTEM-TASKS · 2026-05-20 · scheduler with self-healing
    adminSystemTasks: 'System tasks',
    // Wave 393A · override audit
    adminTrenaOverrides: 'Budget overrides',
    // Wave 392 · storage stats
    adminTrenaStorage: 'Trena Storage',
    // Wave 393B · compositions compat audit
    adminTrenaCompositions: 'Compositions · Compat',
    // Wave QQQ · follow-ups telemetry
    adminFollowUps: 'Follow-ups · Telemetry',
    // B7 · regional overrides
    adminTrenaRegional: 'Compositions · Regional',
    // B9 · assumption chain
    adminTrenaAssumptions: 'Assumptions · Tenant',
    // Settings sub-routes
    settingsNotifications: 'Notification preferences',
    // Wave pm-uom · 2026-05-17 · Unit of measure settings.
    settingsUom: 'Units of measure',
    // W123.4 · 2026-05-26 · 6 PM groups + Settings + segregated Admin.
    hoje: 'Today',
    plano: 'Plan',
    compras: 'Procurement',
    obra: 'Site',
    dinheiro: 'Project costs',
    entrega: 'Handover',
    checklist: 'Checks',
    config: 'Settings',
    comprasGeral: 'Purchases (all projects)', // W149 · 2026-05-28
    comprasGeralDescription: 'Orders, quotes and vendors across all projects', // W149
    dinheiroGeral: 'Financials (all projects)', // W149 · 2026-05-28
    dinheiroGeralDescription: 'Costs, payments and cash flow across all projects', // W149
    admin: {
      comando: 'Command',
      painelAdmin: 'Admin panel',
      saude: 'Health',
      brain: 'Brain',
      autonomia: 'Autonomy',
      deploys: 'Deploys',
      usuarios: 'Users',
      backups: 'Backups',
      auditoria: 'Audit log',
      featureFlags: 'Feature flags',
      labsGenerative: 'Labs · Generative',
      emailCf: 'Email (CF)',
      cron: 'System tasks',
      sinapi: 'SINAPI · Inputs',
      observabilidade: 'Observability',
    },
    modes: {
      campo: 'Field',
      inbox: 'Inbox',
      gestao: 'Manage',
      financeiro: 'Finance',
      qualidade: 'Quality',
      sistema: 'System',
      tudo: 'All',
    },
    showAll: 'Show all',
    showAllOutros: 'Other',
    qualidadeEmpty:
      'Open a project to access inspections, non-conformances and Delivery.',
  },
  // Wave I · Hierarchical sidebar by intent.
  waveI: {
    nav: {
      sections: {
        tocar:     { label: 'Run the project',   description: 'Day-to-day execution: daily logs, drawings, photos, inspections' },
        comprar:   { label: 'Procurement',        description: 'Budget, purchases, quotes, vendors' },
        medir:     { label: 'Measure & estimate', description: 'Estimation, schedule, measurements' },
        qualidade: { label: 'Quality',            description: 'NCRs, audits, cross-project quality' },
        financeiro:{ label: 'Financial',           description: 'Budget, stock, reports' },
        admin:     { label: 'Admin',              description: 'Platform settings (admin only)' },
      },
      items: {
        hoje:              'Today',
        rdos:              'Daily logs',
        pranchas:          'Drawings',
        galeria:           'Gallery',
        fvs:               'Inspections',
        atividade:         'Activity',
        semana:            'This week',
        orcamento:         'Budget',
        compras:           'Purchases',
        cotacoes:          'Quotes',
        fornecedores:      'Vendors',
        leiloes:           'Reverse auctions',
        trena:             'Trena',
        cronograma:        'Schedule',
        medicoes:          'Measurements',
        hubQualidade:      'Quality hub',
        rnc:               'Non-conformances',
        rfi:               'RFI',
        verba:             'Allowance',
        estoque:           'Stock',
        relatorios:        'Reports',
        painelCompras:     'Procurement panel',
        adminHome:         'Admin panel',
        adminCommandControl: 'Command & Control',
        adminSaude:        'Health',
        adminBrain:        'Brain',
        adminAutonomia:    'Autonomy',
        adminUsuarios:     'Users',
        adminComando:      'Command Center',
        adminConfiguracoes: 'Settings',
        adminHistoricos:   'History',
        adminSystemTasks:  'System tasks',
        adminMetaTemplates: 'Meta Templates',
        adminLabsGenerative: 'Labs · Generative Engineering',
        adminLabsGenerativeHistory: 'Lab History',
      },
      noProjectSelected: 'Select a project',
    },
    mobileBottomNav: {
      ariaLabel: 'Main navigation',
      hoje:    'Today',
      rdo:     'Log+',
      galeria: 'Gallery',
      inbox:   'Inbox',
      mais:    'More',
      fechar:  'Close menu',
    },
  },
  campo: {
    title: 'Field',
    subtitle: "What needs you on site today.",
    greeting: (firstName) => `Hi, ${firstName}.`,
    workingOn: 'Working on',
    projectPickerAria: 'Select project',
    noProjectsTitle: 'No project assigned.',
    noProjectsDescription:
      "When your manager adds you to a project, it'll show up here.",
    rdoCard: "Today's daily log",
    rdoCardAria: (projectName) => `Open today's daily log · ${projectName}`,
    rdoMissing: 'Not submitted yet',
    rdoDraft: 'Draft saved · not signed',
    rdoSigned: (hourMinute) => `Submitted at ${hourMinute}`,
    fvsCard: 'Inspections pending today',
    fvsCardAria: (projectName, n) =>
      `${n} pending inspections in ${projectName} · open`,
    fvsPendingSubtitle: (n) =>
      n === 1 ? '1 open item' : `${n} open items`,
    photoFab: 'Photo',
    photoFabAria: (projectName) => `Take a site photo · ${projectName}`,
    photoFabDisabled: 'Select a project to take a photo',
    photoSaved: (projectName) => `Photo saved in ${projectName}.`,
    hojePill: 'Today',
    hojePillAria: (n) =>
      n === 0 ? "Today's queue is empty." : `Today · ${n} pending`,
    allQuietTitle: 'All caught up. Nice work.',
    allQuietDescription:
      "Daily log submitted, no inspections pending. Come back when something needs you.",
    // Inline 3-tap phase
    tabRdo: 'Daily Log',
    tabFotos: 'Photos',
    tabVoz: 'Voice',
    tabFvs: 'Inspection',
    autoSaved: 'Draft auto-saved',
    sendTodayBtn: (n) => n === 0 ? 'Send today' : `Send today (${n} item(s))`,
    publishedOk: 'Daily log published!',
    partialSend: (sent, total) => `${sent} of ${total} sent. The rest stays in the draft.`,
    sendFailed: 'Send failed. Please try again.',
    openFullEditor: 'Full editor →',
    fvsSentOk: (n) => n === 1 ? '1 inspection response sent.' : `${n} inspection responses sent.`,
    fvsSentPartial: (sent, total) => `${sent} of ${total} inspection responses sent. The rest stays in the draft.`,
  },
  semana: {
    title: 'This week',
    subtitle: (range) => `Everything happening ${range}.`,
    weekRangeSeparator: '—',
    thisWeek: 'This week',
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
    dayPicker: 'Pick a day',
    dayShort: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    emptyTitle: 'Quiet week',
    emptyBody:
      'Nothing scheduled this week: no stage starting, no measurement, no inspection, no PO delivery. Use ← → to browse other weeks.',
    dayEmpty: 'Nothing on this day.',
    noProject: 'Pick a project to see this week.',
    kindLabel: {
      stage: 'Stage',
      measurement: 'Measurement',
      fvs: 'Inspection',
      po: 'PO delivery',
    },
    itemLabel: {
      stage: (name) => `Start · ${name}`,
      measurement: (day) => `Measurement starts on day ${day}`,
      fvs: (templateName) => templateName,
      po: (supplierName) => `Delivery · ${supplierName}`,
    },
    itemFallback: {
      fvs: 'Inspection',
      po: 'supplier',
    },
  },
  hoje: {
    title: 'Today',
    subtitle: "What's pending for you right now.",
    loadError: "Couldn't load today's queue.",
    emptyTitle: "Today's queue is clear.",
    emptyDescription:
      'No pending decisions, inspections, daily logs, or attention points. Come back when something needs you.',
    section: {
      aprovacoes: 'Approvals waiting on you',
      diariosAtrasados: 'Overdue daily logs',
      fvsPendentes: 'Inspections pending',
      rncs: 'Corrections (non-conformities) assigned to you',
      // Wave G: human-friendly label for attention points (was 'Anomalies to resolve')
      anomalias: 'Attention points to resolve',
      outros: 'Other',
    },
  },
  projects: {
    title: 'Projects',
    newProject: 'New project',
    empty: 'No projects yet.',
    emptyHint: 'Create your first project to get started.',
    filterAll: 'All',
    filterActive: 'Active',
    filterOnHold: 'On hold',
    filterCompleted: 'Completed',
    stats: {
      inProgress: 'In progress',
      onHold: 'On hold',
      completed: 'Completed',
      overdue: 'Overdue',
    },
    status: {
      planning: 'Planning',
      in_progress: 'In progress',
      on_hold: 'On hold',
      completed: 'Completed',
      archived: 'Archived',
    },
    client: 'Client',
    clientName: 'Client name',
    clientEmail: 'Client email',
    clientPhone: 'Client phone',
    address: 'Address',
    progress: 'Progress',
    dueDate: 'Due date',
    startDate: 'Start',
    completedAt: 'Completed on',
    budget: 'Budget',
    notes: 'Notes',
    createProject: 'Create project',
    backToProjects: 'All projects',
    overallProgress: 'overall progress',
    notStarted: 'Not started',
    couldNotLoad: 'Could not load project.',
    ownerReportBtn: 'Monthly report',
    ownerReportPickMonth: 'Choose report month',
    ownerReportDownloading: 'Generating report…',
    ownerReportError: 'Could not generate report. Please try again.',
    ownerReportEmptyHint: 'No activity yet this month. Data will appear here as soon as you create RDOs and POs.',
    ownerReportRetry: 'Try again',
  },
  stages: {
    title: 'Stages',
    addStage: 'Add stage',
    addFirst: 'Add first stage',
    empty: 'No stages yet.',
    emptyHint: 'Break the build into phases: demo, framing, electrical, finishes, and so on.',
    name: 'Stage name',
    description: 'Description',
    status: 'Status',
    percent: '% complete',
    reorderHint: 'Drag to reorder',
    plannedStart: 'Planned start',
    plannedEnd: 'Planned end',
    target: 'Target',
    start: 'Start',
    done: 'Done',
    reopen: 'Reopen',
    couldNotLoad: 'Could not load stages.',
    couldNotAdd: 'Could not add stage.',
    statuses: {
      not_started: 'Not started',
      in_progress: 'In progress',
      blocked: 'Blocked',
      completed: 'Completed',
    },
  },
  dailyLogs: {
    title: 'Daily logs',
    addLog: 'New entry',
    logToday: 'Log today',
    saveLog: 'Save log',
    draftRestored: (when) => `Draft restored (${when})`,
    discardDraft: 'Discard draft',
    createFirst: 'Create first log',
    empty: 'No logs yet.',
    emptyHint: 'Daily logs capture weather, crew, and the work performed.',
    date: 'Date',
    workPerformed: 'Work performed',
    issues: 'Issues',
    issuesOptional: 'Issues (optional)',
    issuesLabel: 'Issues',
    note: 'Notes',
    notePlaceholder: 'What happened on site today?',
    weather: 'Weather',
    temperatureC: 'Temperature (°C)',
    tempShort: 'Temp °C',
    crewCount: 'Crew size',
    crewShort: 'Crew',
    hoursWorked: 'Hours worked',
    hoursShort: 'Hours',
    postedBy: (name) => `by ${name}`,
    couldNotSave: 'Could not save log.',
    weatherOptions: {
      clear: 'Clear',
      cloudy: 'Cloudy',
      rain: 'Rain',
      storm: 'Storm',
      snow: 'Snow',
      fog: 'Fog',
      wind: 'Wind',
      hot: 'Hot',
      cold: 'Cold',
    },
    rdo: {
      statusDraft: 'Draft',
      statusSigned: 'Signed',
      statusCorrected: 'Superseded',
      signedBy: (name, when) => `Signed by ${name} on ${when}`,
      correctsOriginal: 'Corrects previous entry',
      sign: 'Sign log',
      signing: 'Signing…',
      confirmSignTitle: 'Sign this daily log?',
      confirmSignBody:
        'Once signed, this entry becomes immutable. Corrections must be filed as a new log linked to this one.',
      confirmSignCta: 'Sign now',
      correct: 'File correction',
      correctTitle: 'Correct daily log',
      correctSubtitle:
        'The original entry stays on record. Explain what is being corrected and submit a new, superseding log.',
      correctionNote: 'Reason for correction',
      correctionNotePlaceholder:
        'e.g. the reported crew count was wrong — the correct value is…',
      saveCorrection: 'Save correction',
      couldNotSign: 'Could not sign the daily log.',
      couldNotCorrect: 'Could not file the correction.',
      kpi: {
        title: 'Daily log summary',
        totalLogs: 'Total logs',
        signedLogs: 'Signed logs',
        signedThisMonth: 'Signed this month',
        lastLog: 'Last log',
        lastSigned: 'Last signature',
        never: 'None',
      },
      audioNotes: {
        title: 'Audio notes',
        record: 'Record audio note',
        recording: 'Recording…',
        stopRecording: 'Stop recording',
        uploading: 'Uploading…',
        uploadError: 'Failed to upload audio. Please try again.',
        noAudios: 'No audio notes on this daily log.',
        play: 'Play',
        duration: (seconds: number) => `${seconds}s`,
        holdToRecord: 'Press and hold to record',
        unsupportedBrowser: 'Audio recording not supported in this browser.',
        delete: 'Delete note',
        deleteConfirm: 'Delete this audio note? This cannot be undone.',
        deleteError: 'Could not delete the note. Please try again.',
      },
    },
  },
  timeline: {
    title: 'Timeline',
    empty: 'Nothing posted yet.',
    emptyHint: 'Post photos, milestones, issues, and deliveries as the build progresses.',
    addUpdate: 'New update',
    newUpdate: 'New update',
    postUpdate: 'Post update',
    updatePlaceholder: 'Share an update with the client…',
    attachPhotos: 'Attach photos',
    type: 'Type',
    entryTitle: 'Title',
    entryNotes: 'Notes',
    couldNotLoad: 'Could not load timeline.',
    couldNotPost: 'Could not post update.',
    postedBy: (name) => `by ${name}`,
    types: {
      note: 'Note',
      stage_started: 'Stage started',
      stage_completed: 'Stage completed',
      stage_blocked: 'Stage blocked',
      milestone: 'Milestone',
      issue: 'Issue',
      delivery: 'Delivery',
      inspection: 'Inspection',
      photo: 'Photos',
      daily_log: 'Daily log',
    },
  },
  photos: {
    title: 'Photos',
    upload: 'Upload photo',
    uploading: 'Uploading…',
    addPhotos: 'Add photos',
    empty: 'No photos yet.',
    captionPlaceholder: 'Caption (optional)',
    dropHere: 'Drop photos here',
    photoCount: (n) => (n === 1 ? '1 photo' : `${n} photos`),
    unsupportedType: (type) => `Unsupported file type: ${type || 'unknown'}`,
    tooLarge: (name) => `${name} is larger than 25 MiB`,
    uploadFailed: 'Upload failed.',
  },
  members: {
    title: 'Team',
    invite: 'Invite',
    email: 'Email',
    role: 'Role',
    empty: 'No team members yet.',
    remove: 'Remove',
    confirmRemove: (name) => `Remove ${name} from this project?`,
    pixKey: 'PIX key',
    pixCopy: 'Copy',
    pixCopied: 'Copied',
    noPix: 'No PIX key',
    addTitle: 'Add someone who already has an account', // W148 · 2026-05-28
    addSubtitle: 'Email of someone already registered in Builder — engineer, foreman, technician.', // W148 · 2026-05-28
    addCta: 'Add',
    you: 'You',
    memberCount: (n) => (n === 1 ? '1 member' : `${n} members`),
    joined: (date) => `Joined ${date}`,
    couldNotLoad: 'Could not load team.',
    couldNotAdd: 'Could not add member.',
    couldNotUpdate: 'Could not update role.',
    couldNotRemove: 'Could not remove member.',
    validEmail: 'Please enter a valid email.',
    inviteOwnerTitle: 'Invite a new person', // W148 · 2026-05-28
    inviteOwnerSubtitle:
      'They\'ll receive a link by email to create an account. No email? Generate the link and send via WhatsApp.', // W148 · 2026-05-28
    ownerEmail: 'Invitee email (optional)',
    ownerWhatsapp: 'Invitee WhatsApp (optional)',
    ownerWhatsappPlaceholder: 'e.g. +55 11 99999-9999',
    inviteNoEmailHint: 'No email? Click "Generate invite link" and send it via WhatsApp.',
    generateLink: 'Generate invite link',
    shareLink: 'Send this link to the invitee:',
    copyLink: 'Copy link',
    copied: 'Copied!',
    shareViaWhatsApp: 'Send via WhatsApp',
    inviteEmpty: 'No invitations yet',
    inviteEmptyHint: 'Send an invitation above to give your client access.',
    invitationsTitle: 'Invitations sent',
    accepted: (date) => `Accepted ${date}`,
    expiredOn: (date) => `Expired ${date}`,
    expires: (date) => `Expires ${date}`,
    revoke: 'Revoke',
    couldNotRevoke: 'Could not revoke.',
    couldNotCreateInvitation: 'Could not create invitation.',
    roles: {
      admin: 'Admin',
      project_manager: 'Project manager',
      foreman: 'Foreman',
      worker: 'Worker',
      client: 'Owner',
      broker: 'Supplier', // W152 · unified from "Broker" → "Supplier"
      buyer: 'Buyer', // W149
      controller: 'Controller', // W149
    },
    trade: 'Trade',
    tradePlaceholder: 'e.g. Engineer, Mason, Electrician',
    tradeOptional: 'Trade (optional)',
    additionalRolesLabel: 'Additional roles',
    invitationRole: 'Invitation role',
  },
  owner: {
    welcome: (name) => `Hi, ${name}`,
    yourProjects: 'Your projects',
    portalTitle: 'Client portal',
    subtitle: 'Follow your projects in real time.',
    empty: 'Nothing shared yet.',
    emptyHint:
      'When your contractor links a project to your email, it will show up here.',
    couldNotLoad: 'Could not load your projects.',
    backToPortal: 'All projects',
    stages: 'Stages',
    timeline: 'Updates',
    photos: 'Photos',
    noStages: 'No stages defined yet.',
    noUpdates: 'No updates posted yet.',
    payTo: (name) => `Pay ${name}`,
    paymentMethod: 'Payment method',
    pixLabel: 'PIX',
    pixInstruction: 'Copy the PIX key below to pay the project manager.',
    transparency: 'Transparency',
    pendingIssues: 'Pending Issues',
    anomalies: 'anomalies',
    noncompliances: 'non-compliances',
    verifications: 'inspections',
    autonomyActions: 'Autonomy Actions',
    decisionsMade: 'Decisions Made',
    unreadMessages: 'Unread Messages',
    last30Days: 'last 30 days',
    pendingYourAttention: 'pending your attention',
    recentActivity: 'Recent Activity',
    lastUpdate: 'Last update',
    lastPayment: 'Last payment',
    photosCount: 'Photos shared',
    noUpdatesYet: 'No updates yet',
    maintenanceBanner:
      "🔧 Builder is in maintenance. You're viewing data in read-only mode. Client Portal remains updated.",
  },
  maintenance: {
    title: '🔧 Builder is being refactored',
    body:
      "I'm reorganizing the app so it becomes a real construction workflow instead of a pile of separate screens. Back in a few weeks.",
    bulletsHeader: 'In the meantime:',
    bulletVitrine: 'The Client Portal is still online:',
    bulletApproval:
      "If you're a project owner with an approval link or Owner Portal access, those keep working.",
    bulletContact: 'If something is critical, reach me:',
    signature: '— JP',
    vitrineLinkLabel: 'genese.nullscience.ai',
  },
  invitation: {
    loadingTitle: 'Loading invitation…',
    loadingBody: 'One moment while we validate your link.',
    invalidTitle: 'Invitation unavailable',
    invalidBody: "This link doesn't look right. Ask your project manager for a new one.",
    expiredTitle: 'Invitation expired',
    expiredBody: 'This invitation is past its expiration date. Ask for a fresh one.',
    expiredTitleFor: (projectName) => `Ask for a fresh link to ${projectName}`,
    acceptedTitle: 'Already accepted',
    acceptedBody: (email, projectName) =>
      `The invitation for ${email} has already been accepted. Sign in to follow ${projectName}.`,
    acceptTitle: "You're invited",
    acceptSubtitle: (projectName) =>
      `Follow ${projectName} as an owner — photos, progress, and milestones in one place.`,
    acceptCta: 'Create account and accept',
    acceptExistingCta: 'Accept and sign in',
    accepting: 'Accepting…',
    choosePassword: 'Choose a password',
    existingAccountNote:
      'We found an existing account for this email. Accepting will link this project to it — no new password needed.',
    wrongAccount: 'Wrong account?',
    signInDifferentEmail: 'Sign in with a different email',
    goHome: 'Go home',
    missingToken: 'This invitation link is missing its token.',
  },
  // Wave pm-uom · 2026-05-17 · Custom units of measure.
  uom: {
    pageTitle: 'Units of measure',
    pageSubtitle: 'Manage the units available in budgets and purchases.',
    systemSection: 'System units',
    customSection: 'Your units',
    addCta: 'Add unit',
    codePlaceholder: 'hole, cub-sp, pc…',
    codeHint: 'Short code. Lowercase, no spaces.',
    labelPlaceholder: 'Hole, CUB-SP, Pc…',
    descriptionPlaceholder: 'Optional description',
    categoryLabel: 'Category',
    categories: {
      comprimento: 'Length',
      area: 'Area',
      volume: 'Volume',
      tempo: 'Time',
      massa: 'Mass',
      unidade: 'Unit',
      outro: 'Other',
    },
    badgeSystem: 'system',
    badgeCustom: 'custom',
    badgeInactive: 'inactive',
    deleteConfirm: 'Deactivate this unit? Existing items are not affected.',
    saved: 'Unit saved.',
    deleted: 'Unit deactivated.',
    errorSave: 'Could not save.',
    errorDelete: 'Could not deactivate.',
    empty: 'No custom units yet.',
    emptyHint: 'Add units like "Hole", "CUB-SP" or "Lump sum".',
    codeRequired: 'Code is required.',
    labelRequired: 'Name is required.',
    codeInvalid: 'Code must be lowercase, no spaces (use - or .).',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Manage your account.',
    profile: 'Profile',
    profileSubtitle: 'How your name appears on logs, photos, and updates.',
    changePassword: 'Change password',
    changePasswordSubtitle: 'Use a strong password you don\'t reuse elsewhere.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    passwordsDoNotMatch: 'Passwords don\'t match.',
    avatarUrl: 'Avatar URL',
    avatarHint: 'Paste a link to an externally hosted image.',
    pixKey: 'PIX key',
    pixKeyHint: 'CPF, CNPJ, email, phone, or random key.',
    profileSaved: 'Profile updated.',
    passwordChanged: 'Password updated.',
    couldNotSave: 'Could not save.',
    couldNotChangePassword: 'Could not change password.',
    // Wave iiiii — consolidated settings page sections
    role: 'Role',
    tenantName: 'Organization',
    sectionPerfil: 'Profile',
    sectionPreferencias: 'Preferences',
    sectionNotificacoes: 'Notifications',
    sectionSeguranca: 'Security',
    sectionSobre: 'About',
    langLabel: 'Language',
    themeLabel: 'Theme',
    notifInApp: 'In-app notifications',
    notifEmailSummary: 'Email digest',
    notifEnabled: 'enabled',
    twoFa: 'Two-factor authentication',
    sessionsTitle: 'Active sessions',
    signOutOtherSessions: 'Sign out other sessions',
    signOutOtherSessionsSuccess: 'Other sessions signed out.',
    appVersion: 'Version',
    env: 'Environment',
    deployedAt: 'Deployed',
    supportEmail: 'support@nullscience.ai',
    supportLink: 'https://nullscience.ai/support',
  },
  time: {
    justNow: 'just now',
    minutesAgo: (n) => (n === 1 ? '1 min ago' : `${n} min ago`),
    hoursAgo: (n) => (n === 1 ? '1h ago' : `${n}h ago`),
    daysAgo: (n) => (n === 1 ? '1 day ago' : `${n} days ago`),
    weeksAgo: (n) => (n === 1 ? '1 week ago' : `${n} weeks ago`),
  },
  wave3: {
    tabs: {
      finance: 'Billing',
      docs: 'Documents',
      audit: 'Audit',
    },
    curvaS: {
      title: 'S-curve · Planned vs. Actual',
      planned: 'Planned',
      actual: 'Actual',
      empty: 'No history yet — waiting on the first weekly snapshot.',
      recompute: 'Recompute now',
      recomputing: 'Recomputing…',
      delta: (n) => `Δ ${n} pp`,
    },
    measurements: {
      title: 'Measurements',
      subtitle: 'Periodic per-stage billing. The server rejects over-billing beyond 100% per stage.',
      empty: 'No measurements yet.',
      newCta: 'New measurement',
      status: {
        draft: 'Draft',
        approved: 'Approved',
        sent: 'Sent',
        paid: 'Paid',
        rejected: 'Rejected',
      },
      period: 'Period',
      total: 'Total',
      approvedBy: (name, date) => `Approved by ${name} · ${date}`,
      paidAt: (date) => `Paid on ${date}`,
      approve: 'Approve',
      markPaid: 'Mark as paid',
      viewItems: 'View items',
      overBilling: 'Exceeds 100% for a stage.',
      couldNotCreate: 'Could not create the measurement.',
      createTitle: 'New measurement',
      periodStart: 'Period start',
      periodEnd: 'Period end',
      stage: 'Stage',
      percentBilled: '% billed',
      unitPrice: 'Unit price',
      subtotal: 'Subtotal',
      addRow: 'Add line',
      removeRow: 'Remove',
      notes: 'Notes',
      submit: 'Create measurement',
    },
    purchases: {
      title: 'Purchase requests',
      subtitle: 'Requests from the site, approved by the PM.',
      empty: 'No requests yet.',
      newCta: 'New request',
      status: {
        draft: 'Draft',
        pending: 'Pending approval',
        approved: 'Approved',
        rejected: 'Rejected',
        fulfilled: 'Fulfilled',
        cancelled: 'Cancelled',
      },
      requestedBy: (name, date) => `Requested by ${name} · ${date}`,
      approve: 'Approve',
      reject: 'Reject',
      fulfill: 'Mark fulfilled',
      createTitle: 'New request',
      itemDescription: 'Description',
      itemQuantity: 'Qty',
      itemUnit: 'Unit',
      itemEstimate: 'Estimate',
      rejectPlaceholder: 'Rejection reason (optional)',
    },
    attachments: {
      title: 'Documents',
      subtitle: 'Contracts, ART, permits, licenses, invoices, and reports.',
      empty: 'No documents yet.',
      upload: 'Upload document',
      uploading: 'Uploading…',
      kindLabel: 'Kind',
      expiresAtLabel: 'Expiry',
      kind: {
        contrato: 'Contract',
        art: 'ART',
        alvara: 'Permit',
        licenca: 'License',
        cnpj: 'CNPJ',
        nf: 'Invoice',
        boletim: 'Report',
        other: 'Other',
      },
      expiresAt: (date) => `Expires ${date}`,
      expired: 'Expired',
      expiringSoon: (days) => (days === 1 ? 'Expires tomorrow' : `Expires in ${days} days`),
      couldNotDelete: 'Could not delete.',
      couldNotUpload: 'Upload failed.',
    },
    dependencies: {
      title: 'Stage dependencies',
      subtitle: 'Finish-to-start: the successor can only begin once the predecessor is completed.',
      empty: 'No dependencies yet.',
      predecessor: 'Predecessor',
      successor: 'Successor',
      add: 'Add dependency',
      blockedBy: (names) => `Waiting on: ${names}`,
      couldNotAdd: 'Could not add (cycle or invalid stage).',
    },
    audit: {
      title: 'Audit trail',
      subtitle: 'Immutable hash-chained events — any tampering breaks the chain.',
      empty: 'No events.',
      verifyCta: 'Verify chain',
      chainOk: 'Chain intact ✓',
      chainBroken: (at) => `Chain broken at ${at}`,
      actorSystem: 'system',
      // Wave G: 'Show payload' → human-friendly 'Show technical details'
      showPayload: 'Show technical details',
      hidePayload: 'Hide',
    },
  },
  wave4: {
    tabs: {
      purchases: 'Purchases',
      changeOrders: 'Change orders',
    },
    suppliers: {
      title: 'Suppliers',
      subtitle: 'Directory of suppliers serving this tenant.',
      empty: 'No suppliers yet.',
      new: 'New supplier',
      legalName: 'Legal name',
      displayName: 'Display name',
      displayNameHint: 'Optional — used throughout the app.',
      cnpj: 'CNPJ',
      cnpjHint: '14 digits, no punctuation.',
      email: 'Email',
      phone: 'Phone',
      pixKey: 'PIX key',
      notes: 'Notes',
      archive: 'Archive',
      unarchive: 'Reactivate',
      archivedBadge: 'Archived',
      showArchived: 'Show archived',
      hideArchived: 'Hide archived',
      couldNotSave: 'Could not save supplier.',
      searchPlaceholder: 'Search by name, phone, email, notes…',
      filterAll: 'All',
      filterSanctioned: 'Only sanctioned',
      filterByCategory: 'Filter by category',
      categories: 'Categories',
      categoriesHint: 'Check everything this supplier serves.',
      categoryLabels: {
        materiais: 'Materials (general store)',
        cimento_concreto: 'Cement / Concrete',
        areia_brita: 'Sand / Gravel / Stone',
        ferro_aco: 'Steel / Rebar',
        madeira: 'Wood / Boards',
        hidraulica: 'Plumbing / PVC',
        eletrica: 'Electrical / Cables',
        cobertura: 'Roofing / Tiles',
        argamassa_imperm: 'Mortar / Waterproofing',
        tintas: 'Paints',
        vidro: 'Glass / Mirror',
        esquadrias: 'Doors / Windows',
        aluminio: 'Aluminum',
        ceramica: 'Ceramic / Porcelain',
        metais_sanitarios: 'Sanitary / Faucets',
        isolamento: 'Insulation',
        equipamentos: 'Equipment / Rental',
        servicos: 'Services / Labor',
      },
      noMatchTitle: 'Nothing here',
      noMatchHint: 'Try a different search or clear the filter.',
      countResult: '{count} supplier(s)',
    },
    purchaseOrders: {
      title: 'Purchase orders',
      subtitle: 'Formal supply orders for this project.',
      empty: 'No orders issued.',
      new: 'New order',
      status: {
        draft: 'Draft',
        issued: 'Issued',
        partially_received: 'Partial',
        received: 'Received',
        cancelled: 'Cancelled',
      },
      supplier: 'Supplier',
      selectSupplier: 'Select…',
      addSupplier: '+ New supplier',
      expectedDelivery: 'Expected delivery',
      itemDescription: 'Description',
      itemQuantity: 'Qty',
      itemUnit: 'Unit',
      itemUnitPrice: 'Unit price (R$)',
      itemSubtotal: 'Subtotal',
      addRow: 'Add item',
      tax: 'Tax (R$)',
      shipping: 'Shipping (R$)',
      subtotal: 'Subtotal',
      total: 'Total',
      issue: 'Issue',
      issueConfirm: 'Issue the PO? Items become immutable after issuance.',
      cancel: 'Cancel order',
      cancelReason: 'Cancellation reason',
      issuedAt: (when, who) => `Issued at ${when} by ${who}`,
      receiveCta: 'Record receipt',
      couldNotIssue: 'Could not issue the order.',
      couldNotCancel: 'Could not cancel.',
      couldNotCreate: 'Could not create the order.',
      viewItems: 'View items',
      linkedPr: 'Linked purchase request',
      retentionLabel: 'Retention (%)',
      retentionHint: 'Standard 5% for services; 0% to disable.',
      retentionPlaceholder: '0',
    },
    retention: {
      title: 'Retention held',
      subtitle: 'Warranty collateral withheld from each payment.',
      configured: (pct) => `${pct}% withheld from every payment.`,
      notConfigured: 'This PO has no retention configured.',
      withheld: 'Withheld',
      released: 'Released',
      balance: 'Balance held',
      release: 'Release retention',
      releaseTitle: 'Release retention',
      releaseAmount: 'Amount to release',
      releaseMethod: 'Method',
      releaseCta: 'Release',
      releaseSuccess: 'Release queued. Confirm settlement in Payments.',
      releaseError: 'Could not record the release.',
      noBalance: 'Nothing to release on this PO.',
      netAfterHoldback: (net) => `Net to supplier: ${net}`,
      holdbackChip: (amount) => `Held ${amount}`,
    },
    receipts: {
      title: 'Receipts',
      empty: 'No receipts recorded.',
      new: 'Record receipt',
      receivedAt: 'Received at',
      itemQuantityReceived: 'Received',
      itemQuantityDamaged: 'Damaged',
      itemNotes: 'Notes',
      notesPlaceholder: 'Receipt notes…',
      submit: 'Save',
      receivedBy: (who, when) => `Received by ${who} on ${when}`,
      couldNotCreate: 'Could not save receipt.',
    },
    payments: {
      title: 'Payments',
      subtitle: 'Ledger of financial movements (POs, measurements, refunds).',
      empty: 'No payments recorded.',
      new: 'New payment',
      status: {
        pending: 'Pending',
        settled: 'Settled',
        failed: 'Failed',
        cancelled: 'Cancelled',
      },
      kindLabel: 'Type',
      methodLabel: 'Method',
      kind: {
        po: 'PO',
        measurement: 'Measurement',
        reimbursement: 'Reimbursement',
        refund: 'Refund',
        other: 'Other',
      },
      method: {
        pix: 'PIX',
        boleto: 'Boleto',
        transfer: 'Wire',
        cash: 'Cash',
        other: 'Other',
      },
      amount: 'Amount (R$)',
      amountHint: 'Use a negative value for refunds.',
      occurredAt: 'Date',
      pixTxId: 'PIX tx id',
      reference: 'Reference',
      linkedTo: 'Linked to',
      linkedPo: (code) => `PO ${code}`,
      linkedMeasurement: (period) => `Measurement ${period}`,
      settle: 'Mark as settled',
      settleConfirm: 'Confirm this payment has settled?',
      settledAt: (when) => `Settled at ${when}`,
      recordedBy: (who) => `Recorded by ${who}`,
      couldNotCreate: 'Could not save payment.',
      couldNotSettle: 'Could not settle.',
      submit: 'Save',
      settledTotal: 'Settled',
      pendingTotal: 'Pending',
    },
    changeOrders: {
      title: 'Change orders',
      subtitle: 'Scope changes with schedule and cost impact.',
      empty: 'No change orders yet.',
      new: 'New change order',
      status: {
        draft: 'Draft',
        submitted: 'Submitted',
        approved: 'Approved',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
      },
      codeLabel: 'Code',
      deltaCents: 'Cost delta (R$)',
      deltaCentsHint: 'Negative shrinks the scope.',
      deltaDays: 'Days delta',
      deltaDaysHint: 'Negative shortens schedule.',
      submittedAt: (when) => `Submitted at ${when}`,
      decidedBy: (who, when) => `Decided by ${who} at ${when}`,
      submit: 'Submit for decision',
      approve: 'Approve',
      reject: 'Reject',
      rejectReason: 'Rejection reason',
      couldNotCreate: 'Could not create change order.',
      couldNotSubmit: 'Could not submit.',
      couldNotDecide: 'Could not record decision.',
      // pm-friction P1 · 2026-05-21 — NextActionCard copy for terminal states.
      nextActionApprovedTitle: 'Change order approved',
      nextActionApprovedImpact: (cost, days) =>
        `Baseline updated: ${cost} · ${days}.`,
      nextActionApprovedImpactNeutral: 'Baseline updated.',
      // W123.3 — change order applies for real now.
      nextActionApprovedImpactExplainer:
        'Budget and schedule updated — open budget or schedule to see the changes.',
      nextActionApprovedCta: 'See schedule impact',
      nextActionRejectedTitle: 'Change order rejected',
      nextActionRejectedImpactDefault: 'Baseline is unchanged.',
      nextActionRejectedImpactWithReason: (reason) => `Reason on file: "${reason}".`,
    },
    approvalPage: {
      title: 'Approval link',
      loading: 'Validating token…',
      invalidTitle: 'Invalid link',
      invalidBody: 'This link is not recognized. Ask for a new one.',
      usedTitle: 'Link already used',
      usedBody: 'This token has already been redeemed. Each link is single-use.',
      expiredTitle: 'Link expired',
      expiredBody: 'This link has expired. Ask for a fresh one.',
      confirmTitle: (action) => `Confirm ${action}`,
      confirmApproveBody: (entity) => `You are about to APPROVE: ${entity}.`,
      confirmRejectBody: (entity) => `You are about to REJECT: ${entity}.`,
      confirmCta: 'Confirm',
      cancelCta: 'Cancel',
      reasonLabel: 'Reason (optional)',
      reasonPlaceholder: 'Why?',
      doneTitle: 'All set!',
      doneBody: 'Your decision was recorded. You can close this page.',
      entity: {
        purchase_request: 'Purchase request',
        purchase_order: 'Purchase order',
        measurement: 'Measurement',
        change_order: 'Change order',
      },
      action: {
        approve: 'approval',
        reject: 'rejection',
      },
      goHome: 'Open the app',
    },
  },
  wave10: {
    tabs: {
      issues: 'Issues',
    },
    issues: {
      title: 'Issues',
      subtitle: 'Job-site punch list — log, assign, resolve.',
      empty: 'No open issues.',
      emptyHint: 'Log any job-site punch-list items, adjustments, and corrections. For formal non-conformances requiring documentation, use the Inspections section.',
      newCta: 'New issue',
      createTitle: 'Log an issue',
      titleLabel: 'Title',
      descriptionLabel: 'Description (optional)',
      severityLabel: 'Severity',
      dueDateLabel: 'Due date (optional)',
      stageLabel: 'Related stage',
      assigneeLabel: 'Assignee',
      noneOption: 'None',
      reporterLabel: (name) => `Reported by ${name}`,
      filterStatus: 'Status',
      filterSeverity: 'Severity',
      filterAll: 'All',
      submit: 'Log',
      resolve: 'Resolve',
      reopen: 'Reopen',
      discard: 'Discard',
      markInProgress: 'In progress',
      markBlocked: 'Mark blocked',
      confirmResolve: 'Mark as resolved?',
      resolutionNoteLabel: 'What was done',
      resolutionNoteHint: 'Short note for the audit trail.',
      dueIn: (n) => (n === 1 ? 'Due tomorrow' : `Due in ${n} days`),
      overdueBy: (n) => (n === 1 ? '1 day overdue' : `${n} days overdue`),
      dueToday: 'Due today',
      couldNotCreate: 'Could not log the issue.',
      couldNotUpdate: 'Could not update the issue.',
      severity: {
        baixa: 'Low',
        normal: 'Normal',
        alta: 'High',
        urgente: 'Urgent',
      },
      status: {
        aberta: 'Open',
        em_andamento: 'In progress',
        bloqueada: 'Blocked',
        resolvida: 'Resolved',
        descartada: 'Discarded',
      },
    },
    comments: {
      title: 'Comments',
      empty: 'No comments yet.',
      placeholder: 'Write a comment…',
      post: 'Post',
      posting: 'Posting…',
      postedBy: (name, when) => `${name} · ${when}`,
      deleted: '(removed)',
      delete: 'Delete',
      confirmDelete: 'Delete this comment?',
      visibility: {
        internal: 'Internal',
        owner: 'Client',
        buyer: 'Buyer',
        public: 'Public',
      },
      couldNotPost: 'Could not post comment.',
      couldNotDelete: 'Could not delete.',
    },
    notifications: {
      title: 'Notifications',
      subtitle: 'Everything waiting on you.',
      empty: 'Nothing here yet.',
      unreadCount: (n) =>
        n === 1 ? '1 unread' : `${n} unread`,
      markAllRead: 'Mark all as read',
      markRead: 'Mark read',
      showAll: 'Show all',
      showUnread: 'Unread only',
      couldNotLoad: 'Could not load notifications.',
    },
    admin: {
      title: 'Admin dashboard',
      subtitle: 'Company-wide overview.',
      totalProjects: 'Total projects',
      inProgress: 'In progress',
      openIssues: 'Open issues',
      pendingApprovals: 'Pending approvals',
      overdueDocs: 'Overdue documents',
      recentEvents: 'Recent events',
      noEvents: 'No recent events.',
      noAccess: 'Restricted to account admins.',
    },
    externalLinks: {
      title: 'External links',
      subtitle: 'Client and buyer portals.',
      empty: 'No links issued yet.',
      new: 'New link',
      roleLabel: 'Role',
      roleOwner: 'Client (whole project)',
      roleBuyer: 'Buyer (single unit)',
      unitIdLabel: 'Unit',
      unitIdHint: 'e.g. unit-101',
      recipientEmailLabel: 'Recipient email (optional)',
      recipientPhoneLabel: 'Phone (optional)',
      ttlDaysLabel: 'Validity (days)',
      ttlDaysHint: 'Between 1 and 365.',
      submit: 'Generate link',
      copiedLink: 'Link copied!',
      copyLink: 'Copy link',
      revoke: 'Revoke',
      revoked: 'Revoked',
      expires: (date) => `Expires on ${date}`,
      expired: (date) => `Expired on ${date}`,
      viewCount: (n) => (n === 1 ? '1 view' : `${n} views`),
      lastViewed: (when) => `Last viewed: ${when}`,
      neverViewed: 'Never viewed',
      includeRevoked: 'Include revoked',
      couldNotCreate: 'Could not create link.',
      couldNotRevoke: 'Could not revoke.',
    },
    buyerPortal: {
      loadingTitle: 'Loading your portal…',
      loadingBody: 'Validating access link.',
      invalidTitle: 'Invalid link',
      invalidBody: 'This link is not recognized. Request a new one.',
      expiredTitle: 'Link expired',
      expiredBody: 'This link has expired. Ask the builder to send a new one.',
      revokedTitle: 'Link revoked',
      revokedBody: 'This link has been revoked. Please contact the builder.',
      progressLabel: 'Progress',
      stagesTitle: 'Project stages',
      recentUpdatesTitle: 'Recent updates',
      noStages: 'No stages yet.',
      noUpdates: 'No recent updates.',
      welcome: (clientName) => `Hello, ${clientName}`,
      roleBadgeBuyer: 'Buyer',
      roleBadgeOwner: 'Client',
    },
    bottomTabs: {
      ariaLabel: 'Primary navigation',
      // Wave 10 keys retained for the read-only branch and any
      // legacy consumer. `obras`/`fornecedores`/`admin` are no
      // longer rendered by crew tabs in Wave 352-A.
      obras: 'Projects',
      fornecedores: 'Vendors',
      admin: 'Admin',
      owner: 'Mine',
      // Wave 352-A · `inbox` now means the unified Wave 351-C
      // surface. `avisos` is the read-only label for the
      // bell-style notifications page (`/notificacoes`). `campo`
      // and `hoje` are the new crew-only tabs.
      inbox: 'Inbox',
      avisos: 'Notices',
      campo: 'Field',
      hoje: 'Today',
      eu: 'Me',
    },
    offline: {
      bannerTitle: "You're offline — we'll sync as soon as you're back.",
      bannerHint: 'The app keeps working in read-only mode.',
    },
    uploads: {
      title: 'Uploads',
      active: (n) => (n === 1 ? 'Uploading 1 file…' : `Uploading ${n} files…`),
      errored: (n) => (n === 1 ? '1 upload failed' : `${n} uploads failed`),
      queued: 'Queued…',
      uploaded: 'Uploaded',
      done: 'All done',
      clearDone: 'Clear',
    },
    approval: {
      requestedBy: (name) => `requested by ${name}`,
      requestedAt: (when) => `Requested at ${when}`,
    },
    portal: {
      poweredBy: 'Builder · Follow your build from your phone',
    },
  },

  wave11: {
    tabs: {
      budget: 'Budget',
    },
    budget: {
      title: 'Project budget',
      subtitle:
        'Plan vs. actual. Actuals come from approved POs tagged with the category.',
      empty:
        'No budget items yet. Create the first one to start tracking planned vs. actual.',
      addItem: 'Add item',
      addItemHint: 'Describe the item briefly and the planned amount in BRL.',
      descriptionPlaceholder: 'Ex.: Cement CP-II 50kg (50 bags)',
      plannedPlaceholder: 'R$ 0.00',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving…',
      includeArchived: 'Include archived',
      category: 'Category',
      description: 'Description',
      planned: 'Planned',
      // W123.9 · JP-13 — "Actual" was ambiguous (PO issued + paid). Renamed
      // to "Committed" (active POs + manual expenses). "Paid" / "Saldo"
      // are now separate columns.
      actual: 'Committed',
      paid: 'Paid',
      saldo: 'Balance',
      variance: 'Variance',
      varianceFavorable: (amount) => `${amount} under`,
      varianceUnfavorable: (amount) => `${amount} over`,
      varianceOnTrack: 'On plan',
      varianceNoActual: 'No actuals yet',
      usage: (percent) => `${percent}% used`,
      usageOver: 'No plan',
      poCount: (count) =>
        count === 1 ? '1 order' : `${count} orders`,
      totals: 'Totals by category',
      grandPlanned: 'Total planned',
      grandActual: 'Total committed',
      grandPaid: 'Total paid',
      grandSaldo: 'Balance',
      untagged: 'Uncategorized',
      untaggedHint:
        'Approved POs with no category. Edit a PO to tag it with a budget item.',
      archived: 'Archived',
      archive: 'Archive',
      unarchive: 'Reactivate',
      removeConfirm:
        'Remove this item? Linked POs remain — only this budget row disappears.',
      categories: {
        obra: 'Construction',
        material: 'Material',
        mao_obra: 'Labor',
        servico: 'Service',
        equipamento: 'Equipment',
        outros: 'Other',
      },
      errors: {
        descriptionTooShort: 'Description must have at least 2 characters.',
        plannedNegative: 'Planned amount cannot be negative.',
        loadFailed: 'Could not load the budget.',
      },
    },
    audit: {
      title: 'History',
      close: 'Close',
      empty: 'No events here yet.',
      verifyChain: 'Verify chain',
      chainOk: 'Chain intact — no events were tampered with.',
      chainBrokenAt: (id) => `Break detected at event ${id}.`,
      loadFailed: 'Could not load history.',
    },
    excelImport: {
      importBtn: 'Import Excel',
      exportBtn: 'Export Excel',
      pageTitle: 'Import budget spreadsheet',
      uploadStep: '1. Upload',
      mappingStep: '2. Columns',
      diffStep: '3. Review',
      confirmStep: '4. Confirm',
      dropzone: 'Drop your .xlsx file here',
      or: 'or',
      browse: 'browse files',
      acceptedFormats: 'Accepts .xlsx (Excel 2007+)',
      analyzing: 'Analyzing spreadsheet…',
      columnHeader: 'Spreadsheet column',
      fieldLabel: 'Budget field',
      confidence: 'Confidence',
      ignore: 'Ignore',
      addsTitle: (n) => `Add ${n} row(s)`,
      updatesTitle: (n) => `Update ${n} row(s)`,
      conflictsTitle: (n) => `${n} conflict(s)`,
      conflictNote: 'These rows will not be imported — the existing total differs from the calculated one. Resolve manually.',
      applyBtn: (n) => `Apply ${n} change(s)`,
      applying: 'Applying…',
      successToast: (adds, updates) =>
        `Budget updated: +${adds} added, ${updates} updated.`,
      cancel: 'Cancel',
      back: 'Back',
      next: 'Next',
      noChanges: 'No changes detected. The file is in sync with the current budget.',
      previewRows: 'Preview (first 20 rows)',
      sheetInfo: (sheet, rows) => `Sheet "${sheet}" · ${rows} data row(s)`,
      mappingHint: 'Review column mapping. Use "Ignore" to skip a column.',
      warnings: 'Warnings',
    },
  },

  wave12: {
    search: {
      label: 'Search',
      placeholder: 'Search projects, RDO, issues, suppliers…',
      shortcut: 'Ctrl K',
      noResults: 'Nothing found.',
      loading: 'Searching…',
      recent: 'Recently opened',
      openPalette: 'Open search',
      groups: {
        projects: 'Projects',
        dailyLogs: 'Daily logs',
        issues: 'Issues',
        suppliers: 'Suppliers',
        purchaseOrders: 'Purchase orders',
      },
    },
    rdoPdf: {
      export: 'Export PDF',
      generating: 'Generating PDF…',
      title: (projectName) => `RDO — ${projectName}`,
      generatedAt: (when) => `Generated at ${when}`,
      date: 'Date',
      weather: 'Weather',
      crewSize: 'Crew',
      hoursWorked: 'Hours',
      temperature: 'Temperature',
      workPerformed: 'Work performed',
      issues: 'Issues',
      author: 'Author',
      footer: 'Builder · Daily log',
      fileName: (projectName, date) =>
        `rdo-${projectName}-${date}.pdf`.replace(/\s+/g, '-').toLowerCase(),
    },
    pwa: {
      installTitle: 'Install Builder on your phone?',
      installBody:
        'Add to home screen for full-screen mode and notifications.',
      installCta: 'Install',
      later: 'Not now',
      installed: 'App installed',
    },
  },

  commandBar: {
    placeholder: 'Search across the platform…',
    noResults: 'Nothing found.',
    loading: 'Searching…',
    recent: 'Recently visited',
    groups: {
      projects: 'Projects',
      estimates: 'Estimates',
      budgetItems: 'Budget items',
      pos: 'Purchase orders',
      suppliers: 'Suppliers',
      rdos: 'Daily logs',
      fvs: 'Inspections',
      drawings: 'Drawings',
      rfis: 'RFIs',
    },
  },

  wave14: {
    tabs: {
      dashboard: 'Overview',
    },
    dashboard: {
      title: 'Project overview',
      subtitle:
        'KPIs, planned vs. actual S-curve, and the latest events in one place.',
      hero: {
        eyebrow: 'Project dashboard',
        progressLabel: 'Overall progress',
        lastUpdated: (when) => `Updated ${when}`,
      },
      kpi: {
        progress: 'Progress',
        progressHint: (percent) =>
          percent >= 100
            ? 'Project complete'
            : percent === 0
              ? 'Not started yet'
              : `${100 - percent}% left to delivery`,
        budgetUsage: 'Budget used',
        budgetUsageHint: (actual, planned) => `${actual} of ${planned}`,
        budgetEmpty: 'No budget defined',
        openPos: 'Open purchase orders',
        openPosHint: (count) =>
          count === 1
            ? '1 PO awaiting receipt'
            : `${count} POs awaiting receipt`,
        openIssues: 'Open issues',
        openIssuesHint: (count) =>
          count === 0
            ? 'No open issues'
            : count === 1
              ? '1 issue in progress'
              : `${count} issues in progress`,
        expiringDocs: 'Docs expiring (30d)',
        expiringDocsHint: (count) =>
          count === 1
            ? '1 document expires in the next 30 days'
            : `${count} documents expire in the next 30 days`,
        noExpiring: 'No expiring documents',
        lastRdo: 'Last daily log',
        lastRdoHint: (when) => `Signed on ${when}`,
        noRdo: 'No daily log yet',
      },
      curve: {
        title: 'S-curve (planned × actual)',
        subtitle:
          'Planned is derived from project dates; actual comes from executed budget.',
        planned: 'Planned',
        actual: 'Actual',
        empty:
          'Set start/end dates and a budget to draw the curve.',
        axisStart: 'Start',
        axisEnd: 'Delivery',
      },
      activity: {
        title: 'Recent activity',
        subtitle: 'Last 20 events recorded on this project.',
        empty: 'Nothing here yet — as work happens, events will appear.',
        loadFailed: 'We could not load the events.',
        viewAll: 'View full audit trail',
      },
      errors: {
        loadFailed: 'Could not load the dashboard.',
      },
    },
    boletim: {
      title: 'Monthly billing report',
      subtitle:
        'Rolls up approved measurements, issued POs and payments for the month into a client-ready PDF.',
      monthLabel: 'Reference month',
      generateButton: 'Generate monthly PDF',
      generating: 'Generating report…',
      loadFailed: 'Could not load the report.',
      emptyMonth:
        'No approved measurements, POs or payments this month. The PDF will still be generated.',
      pdf: {
        docTitle: (project, month) => `Monthly billing report — ${project} · ${month}`,
        headerLabel: 'Monthly billing report',
        periodLabel: (start, end) => `Period: ${start} to ${end}`,
        clientLabel: 'Client',
        addressLabel: 'Address',
        generatedAt: (when) => `Generated ${when}`,
        measurementsTitle: 'Approved measurements',
        measurementsEmpty: 'No approved measurements this month.',
        poTitle: 'Issued purchase orders',
        poEmpty: 'No purchase orders issued this month.',
        paymentsTitle: 'Payments',
        paymentsEmpty: 'No payments recorded this month.',
        totalsTitle: 'Monthly totals',
        totalMeasurements: 'Measurements total',
        totalPos: 'POs total',
        totalPaymentsSettled: 'Settled payments',
        grandTotal: 'Billable this month (measurements + POs)',
        signaturesTitle: 'Sign-off',
        signClient: 'Client',
        signContractor: 'Contractor',
        footer: 'Builder · Monthly billing report',
        fileName: (projectSlug, month) => `boletim-${projectSlug}-${month}.pdf`,
        colPeriod: 'Period',
        colStatus: 'Status',
        colTotal: 'Total',
        colApprovedBy: 'Approved by',
        colCode: 'Code',
        colSupplier: 'Supplier',
        colTitle: 'Description',
        colIssuedAt: 'Issued',
        colKind: 'Kind',
        colMethod: 'Method',
        colOccurredAt: 'Date',
        colAmount: 'Amount',
      },
    },
  },

  wave15: {
    dre: {
      title: 'Project P&L',
      subtitle:
        'Earned revenue, committed costs and realized cash flow for the selected period.',
      fromLabel: 'From',
      toLabel: 'To',
      clearRange: 'Clear range',
      loadFailed: 'Could not load the P&L. Try again.',
      allTime: 'Since project start',
      hero: {
        marginEyebrow: 'Gross margin',
        grossProfit: 'Gross profit',
        profit: 'Profit',
        loss: 'Loss',
      },
      revenue: {
        heading: 'Revenue',
        measurements: (count) =>
          count === 1 ? '1 approved measurement' : `${count} approved measurements`,
        changeOrders: (count) =>
          count === 1 ? '1 approved change order' : `${count} approved change orders`,
        total: 'Total revenue',
      },
      costs: {
        heading: 'Costs',
        purchaseOrders: (count) =>
          count === 1 ? '1 purchase order' : `${count} purchase orders`,
        otherExpenses: (count) =>
          count === 1 ? '1 operating expense' : `${count} operating expenses`,
        refunds: (count) =>
          count === 1 ? '1 refund' : `${count} refunds`,
        total: 'Total costs',
      },
      cash: {
        heading: 'Cash flow (realized)',
        subtitle: 'Only settled payments are included.',
        receivedFromClient: 'Received from client',
        paidToSuppliers: 'Paid to suppliers',
        otherOutflows: 'Other outflows',
        refunds: 'Refunds',
        net: 'Net cash',
      },
      generatedAt: (when) => `Generated ${when}`,
    },
    cronograma: {
      title: 'Schedule (physical × financial)',
      subtitle:
        'Cumulative S-curve: planned vs actual physical progress, and planned vs committed vs paid spend.',
      granularityMonth: 'Monthly',
      granularityWeek: 'Weekly',
      loadFailed: 'Could not load the schedule.',
      empty: 'No data for the project window.',
      emptyHint:
        'Set start date and estimated end, and record measurements to see the S-curve.',
      legend: {
        physicalPlanned: 'Physical — planned',
        physicalActual: 'Physical — actual',
        financialPlanned: 'Financial — planned',
        financialCommitted: 'Committed',
        financialPaid: 'Paid',
      },
      axis: {
        physical: 'Physical progress',
        financial: 'Cumulative spend',
      },
      totals: {
        budget: 'Budget',
        committed: 'Committed',
        paid: 'Paid',
        commitmentOfBudget: (percent) => `${percent}% of budget`,
        paidOfBudget: (percent) => `${percent}% of budget`,
      },
      bucketCount: (n) => (n === 1 ? '1 bucket' : `${n} buckets`),
      generatedAt: (when) => `Generated ${when}`,
    },
  },

  wave17: {
    portfolio: {
      title: 'Portfolio',
      subtitle:
        'Every project you follow, rolled up: KPIs, financial health, and at-risk jobs first.',
      empty: 'No projects yet.',
      emptyHint: 'Create the first project to start seeing the portfolio here.',
      loadFailed: 'Could not load the portfolio.',
      hero: {
        eyebrow: 'Portfolio dashboard',
        lastUpdated: (when) => `Updated ${when}`,
      },
      kpi: {
        activeProjects: 'Active projects',
        activeProjectsHint: (active, total) =>
          total === 1
            ? active === 1
              ? '1 of 1 running'
              : '0 of 1 running'
            : `${active} of ${total} running`,
        avgProgress: 'Average progress',
        avgProgressHint: (percent) =>
          percent === 0
            ? 'No progress logged yet'
            : percent >= 100
              ? 'Projects completed'
              : `Across active projects: ${percent}%`,
        atRisk: 'Projects at risk',
        atRiskHint: (count) =>
          count === 0
            ? 'Everything on track'
            : count === 1
              ? '1 project needs attention'
              : `${count} projects need attention`,
        budget: 'Total budget',
        budgetHint: (committed) => `${committed} committed`,
        committed: 'Committed',
        committedHint: (percent) => `${percent} of budget`,
        paid: 'Paid',
        paidHint: (percent) => `${percent} of budget`,
        receivables: 'Receivables',
        receivablesHint: (count) =>
          count === 0
            ? 'No measurements awaiting payment'
            : count === 1
              ? '1 measurement awaiting payment'
              : `${count} measurements awaiting payment`,
        openIssues: 'Critical issues',
        openIssuesHint: (count) =>
          count === 0
            ? 'No high/urgent issues open'
            : count === 1
              ? '1 high/urgent issue open'
              : `${count} high/urgent issues open`,
      },
      redactedBanner:
        'Financial figures hidden — your access is read-only (client/broker).',
      projectsHeading: 'Projects',
      projectsSubtitle:
        'At-risk projects surface first, then active, then completed.',
      atRiskBadge: 'At risk',
      statusLabel: (status) => {
        const map: Record<string, string> = {
          planning: 'Planning',
          in_progress: 'In progress',
          on_hold: 'On hold',
          completed: 'Completed',
          archived: 'Archived',
        };
        return map[status] ?? status;
      },
      progressLabel: (percent) => `${percent}% complete`,
      budgetLabel: (used, total) => `${used} / ${total}`,
      noBudget: 'No budget set',
      openIssuesBadge: (count) =>
        count === 1 ? '1 issue' : `${count} issues`,
      pendingMeasurementsBadge: (count) =>
        count === 1 ? '1 pending measurement' : `${count} pending measurements`,
      overdueHint: (date) => `Overdue since ${date}`,
      openProject: 'Open project',
      exportCsv: 'Export CSV',
      exportCsvHint: 'Download the whole portfolio as UTF-8 CSV — one row per project.',
      exportCsvBusy: 'Generating CSV…',
      exportCsvFailed: 'Could not generate the CSV. Try again.',
    },
  },

  wave18: {
    fluxoCaixa: {
      title: 'Cash flow',
      subtitle: 'Monthly projection with realized inflows, outflows, and upcoming measurements.',
      loadFailed: 'Unable to load the cash flow for this project.',
      empty: 'No movement in this window.',
      emptyHint: 'Add purchase orders, measurements, and payments to see the forecast.',
      hero: {
        eyebrow: '12-month projection',
        lastUpdated: (when) => `Updated ${when}`,
      },
      kpi: {
        realizedIn: 'Realized in',
        realizedOut: 'Realized out',
        projectedIn: 'Projected in',
        projectedOut: 'Projected out',
        netWindow: 'Window net',
        endingBalance: 'Ending balance',
      },
      series: {
        realizedInflow: 'Realized inflow',
        realizedOutflow: 'Realized outflow',
        projectedInflow: 'Projected inflow',
        projectedOutflow: 'Projected outflow',
        cumulative: 'Cumulative',
      },
      table: {
        month: 'Month',
        realizedIn: 'Realized in',
        realizedOut: 'Realized out',
        projectedIn: 'Projected in',
        projectedOut: 'Projected out',
        net: 'Net',
        cumulative: 'Cumulative',
        currentMonth: 'Current',
      },
    },
  },

  wave19: {
    alertas: {
      title: 'Alerts',
      subtitle: 'What needs your attention now, grouped by type and by project.',
      loadFailed: 'Unable to load alerts.',
      empty: 'All clear.',
      emptyHint: 'Nothing urgent across your projects right now.',
      groups: {
        docsExpiring: 'Documents expiring',
        overduePOs: 'Overdue purchase orders',
        pendingApprovals: 'Pending approvals',
        overdueMeasurements: 'Overdue measurements',
        stalledStages: 'Stalled stages',
      },
      severity: {
        critical: 'Critical',
        warning: 'Warning',
        info: 'Info',
      },
      countBadge: (n) => (n === 1 ? '1 item' : `${n} items`),
      goToProject: 'Open project',
    },
  },

  wave21: {
    supplier360: {
      title: 'Supplier 360',
      back: 'Back to suppliers',
      loadFailed: 'Unable to load this supplier.',
      notFound: 'Supplier not found.',
      noActivity: 'No activity for this supplier across your projects yet.',
      noActivityHint:
        'Purchase orders and payments linked to this supplier will appear here.',
      archivedBadge: 'Archived',
      kpis: {
        totalCommitted: 'Committed',
        totalPaid: 'Paid',
        outstanding: 'Outstanding',
        activePOs: 'Active POs',
        completedPOs: 'Received POs',
        cancelledPOs: 'Cancelled',
        onTime: 'On-time delivery',
        onTimeNa: 'No data',
        lastActivity: 'Last activity',
        lastActivityNever: 'No activity yet',
      },
      byProject: {
        title: 'By project',
        subtitle: 'How this supplier is distributed across projects you can see.',
        empty: 'No projects with activity for this supplier.',
        columnProject: 'Project',
        columnCommitted: 'Committed',
        columnPaid: 'Paid',
        columnOutstanding: 'Outstanding',
        columnActive: 'Active',
        activeCount: (n) => (n === 1 ? '1 PO' : `${n} POs`),
      },
      recentPOs: {
        title: 'Recent POs',
        subtitle: 'Most recent 20 purchase orders issued to this supplier.',
        empty: 'No purchase orders issued yet.',
        columnCode: 'Code',
        columnProject: 'Project',
        columnStatus: 'Status',
        columnTotal: 'Total',
        columnIssued: 'Issued',
        columnExpected: 'Expected',
        columnReceived: 'Received',
        onTime: 'On time',
        late: 'Late',
        pending: 'Pending',
      },
      redactedNote:
        'Financial figures hidden for your access level.',
    },
  },

  wave22: {
    approvals: {
      title: 'Approvals inbox',
      subtitle:
        'Everything waiting on your decision across every project.',
      back: 'Back',
      loadFailed: 'Could not load the approvals inbox.',
      empty: 'No open approvals.',
      emptyHint:
        'When someone submits a purchase request, draft purchase order, change order, or measurement for review, it shows up here.',
      filters: {
        all: 'All',
        purchase_request: 'Purchase requests',
        purchase_order: 'Purchase orders',
        change_order: 'Change orders',
        measurement: 'Measurements',
      },
      counts: {
        total: 'Total',
        critical: 'Critical',
        warning: 'Attention',
        info: 'Recent',
      },
      severity: {
        critical: 'Critical',
        warning: 'Attention',
        info: 'Recent',
      },
      byProject: {
        title: 'By project',
        subtitle: 'Where approvals are piling up.',
        countLabel: (n: number) => (n === 1 ? '1 pending' : `${n} pending`),
      },
      list: {
        columnType: 'Type',
        columnCode: 'Code',
        columnProject: 'Project',
        columnSubmitted: 'Submitted',
        columnRequester: 'Requester',
        columnAmount: 'Amount',
        columnWaiting: 'Waiting',
        waitingDays: (n: number) => (n === 1 ? '1 day' : `${n} days`),
        waitingToday: 'Today',
        openLink: 'Open',
      },
      redactedNote:
        'Financial figures hidden for your access level.',
      batch: {
        selectAll: 'Select all',
        selectItem: 'Select item',
        selectedCount: (n: number, total: number) =>
          `${n} of ${total} selected`,
        clear: 'Clear',
        approveSelected: 'Approve selected',
        rejectSelected: 'Reject selected',
        confirmApprove: (n: number, totalLabel: string) =>
          n === 1
            ? `Approve 1 selected item, totaling ${totalLabel}? This releases the spend.`
            : `Approve ${n} selected items, totaling ${totalLabel}? This releases the spend.`,
        confirmReject: (n: number) =>
          n === 1
            ? 'Reject 1 selected item? This cannot be undone.'
            : `Reject ${n} selected items? This cannot be undone.`,
        resultSummary: (ok: number, failed: number) =>
          failed === 0
            ? `${ok} succeeded.`
            : `${ok} succeeded · ${failed} failed.`,
      },
    },
  },

  wave23: {
    activity: {
      title: 'Activity',
      subtitle:
        'What happened on your projects — everything recorded, in order.',
      loadFailed: 'Could not load activity.',
      empty: 'No records in this window.',
      emptyHint: 'Events show up here as soon as something moves on a project.',
      windowLabel: (days: number) =>
        days === 1 ? 'Last 24 hours' : `Last ${days} days`,
      windowOption7: '7 days',
      windowOption14: '14 days',
      windowOption30: '30 days',
      filters: {
        all: 'All',
        rdo: 'Daily logs',
        purchase: 'Purchases',
        change_order: 'Change orders',
        measurement: 'Measurements',
        docs: 'Docs',
        admin: 'Admin',
        other: 'Other',
      },
      counts: {
        total: 'Total',
        rdo: 'Daily logs',
        purchase: 'Purchases',
        change_order: 'Change orders',
        measurement: 'Measurements',
        docs: 'Docs',
      },
      byProject: {
        title: 'By project',
        subtitle: 'Events in the window, grouped by project.',
        countLabel: (n: number) => (n === 1 ? '1 event' : `${n} events`),
      },
      eventTypes: {
        'rdo.created': 'Daily log created',
        'rdo.signed': 'Daily log signed',
        'rdo.correction_created': 'Daily log corrected',
        'purchase_request.created': 'Purchase request created',
        'purchase_request.approved': 'Purchase request approved',
        'purchase_request.rejected': 'Purchase request rejected',
        'purchase_request.fulfilled': 'Purchase request fulfilled',
        'purchase_order.created': 'Purchase order created',
        'purchase_order.issued': 'Purchase order issued',
        'purchase_order.cancelled': 'Purchase order cancelled',
        'receipt.created': 'Receipt recorded',
        'payment.created': 'Payment booked',
        'payment.settled': 'Payment settled',
        'payment.retention_release': 'Retention released',
        'supplier.created': 'Supplier added',
        'supplier.updated': 'Supplier updated',
        'change_order.created': 'Change order created',
        'change_order.submitted': 'Change order submitted',
        'measurement.created': 'Measurement created',
        'measurement.approved': 'Measurement approved',
        'measurement.paid': 'Measurement paid',
        'attachment.uploaded': 'Attachment uploaded',
        'attachment.deleted': 'Attachment removed',
        'comment.created': 'Comment',
        'comment.deleted': 'Comment removed',
        'issue.created': 'Issue opened',
        'issue.deleted': 'Issue removed',
        'external_link.created': 'Link shared',
        'external_link.revoked': 'Link revoked',
        'budget_item.created': 'Budget item added',
        'budget_item.deleted': 'Budget item removed',
        'budget_item.over_plan': 'Budget item over plan',
        'progress.snapshot_recorded': 'Progress recorded',
        'approval_token.used': 'Approved via link',
        'admin.user_created': 'User created',
        'admin.user_updated': 'User updated',
        'admin.user_disabled': 'User disabled',
        'admin.user_force_reset': 'Forced password reset',
        'auth.password_reset_requested': 'Password reset requested',
        'auth.password_reset_completed': 'Password reset completed',
        // W97.5 · system events that previously leaked as raw codes.
        'budget_item.ia_accepted': 'AI accepted budget line',
        'budget_item.ia_rejected': 'AI dismissed budget line',
        'budget_approval.approved': 'Budget approved',
        'budget_approval.rejected': 'Budget rejected',
        'po_overdue_flagged': 'Purchase order overdue — alert raised',
        'po_overdue.flagged': 'Purchase order overdue — alert raised',
        'rdo_draft_stale_flagged': 'Daily log draft idle for 7+ days',
        'rdo_draft_stale.flagged': 'Daily log draft idle for 7+ days',
        'trena_funnel.stage1.entered': 'Trena · estimate started',
        'trena_funnel.stage1.upload_started': 'Trena · uploading file',
        'trena_funnel.stage1.upload_completed': 'Trena · file uploaded',
        'trena_funnel.stage1.extract_completed': 'Trena · measurements extracted',
        'trena_funnel.stage2.entered': 'Trena · reviewing measurements',
        'trena_funnel.stage3.entered': 'Trena · building composition',
        'trena_funnel.stage4.entered': 'Trena · reviewing prices',
        'trena_funnel.stage5.entered': 'Trena · finalizing estimate',
        'trena_funnel.completed': 'Trena · estimate completed',
      },
      // W97.5 — friendly fallbacks for unmapped event codes.
      unknownEvent: (code: string) => {
        const lower = code.toLowerCase();
        if (lower.includes('overdue')) return 'Overdue';
        if (lower.includes('stale')) return 'Idle draft';
        if (lower.includes('ia_accepted')) return 'AI accepted';
        if (lower.includes('ia_rejected')) return 'AI dismissed';
        if (lower.includes('approved')) return 'Approved';
        if (lower.includes('rejected')) return 'Rejected';
        const tail = code.split('.').pop()?.replace(/_/g, ' ') ?? code;
        return tail.charAt(0).toUpperCase() + tail.slice(1);
      },
      bySystem: 'System',
      relativeToday: 'Today',
      relativeYesterday: 'Yesterday',
    },
  },

  wave24: {
    nfe: {
      sectionTitle: 'Import NFe',
      sectionSubtitle:
        'Upload the supplier invoice XML — we create the payment row automatically with the SEFAZ-validated fields.',
      dropHint: 'Drop the XML here or',
      chooseFile: 'choose a file',
      selectedFile: (name: string) => `Selected: ${name}`,
      parseButton: 'Preview',
      ingestButton: 'Import and create payment',
      cancelButton: 'Cancel',
      parsing: 'Reading XML…',
      ingesting: 'Importing…',
      parsedOk: 'XML parsed. Review the data before importing.',
      ingestedOk: 'NFe imported successfully.',
      chaveAcesso: 'Access key',
      cnpjEmitente: 'Issuer CNPJ',
      razaoSocial: 'Legal name',
      nomeFantasia: 'Trade name',
      numeroSerie: 'Nº / Series',
      dataEmissao: 'Issued on',
      valorTotal: 'Total amount',
      numeroProtocolo: 'Protocol',
      noProtocol: '— (NFe not yet authorized)',
      supplierMatchOk: 'CNPJ matches the PO supplier.',
      supplierMatchWarn:
        "Issuer CNPJ differs from the PO supplier — verify before settling.",
      duplicateError:
        'This NFe was already imported on this tenant; no duplicate payment created.',
      parseError: (reason: string) => `Failed to read the XML: ${reason}`,
      methodLabel: 'Default payment method',
      methodHint:
        "Just a guess — you'll adjust on settlement. NFe received ≠ money moved.",
      methodTransfer: 'Bank transfer',
      methodPix: 'PIX',
      methodBoleto: 'Boleto',
      methodCash: 'Cash',
      methodOther: 'Other',
      paymentCreatedHeader: 'Payment created',
      paymentCreatedBody: (ref: string) =>
        `Pending row added to the finance ledger (${ref}). Open settlement to confirm method and amounts.`,
    },
  },

  wave25: {
    share: {
      sectionTitle: 'Share link',
      medicaoSubtitle:
        'Public link with the measurement total — no login, no internal costs.',
      dreSubtitle:
        'Public link with revenue, cost, profit and margin — no supplier detail.',
      empty: 'No active links',
      ttlLabel: 'Valid for',
      ttl7: '7 days',
      ttl14: '14 days',
      ttl30: '30 days',
      createButton: 'Create link',
      createFailed: 'Could not create the link. Try again.',
      tokenShowOnce: 'Copy now — the link is already on your clipboard',
      copyButton: 'Copy',
      copied: 'Copied',
      expiresAt: (when: string) => `Expires ${when}`,
      views: (n: number) =>
        n === 0 ? 'No views yet' : n === 1 ? '1 view' : `${n} views`,
      revokeButton: 'Revoke',
      revokeConfirm: 'Confirm revoke',
      revokeCancel: 'Cancel',
    },
    public: {
      header: 'Shared via Builder',
      footer: (projectName: string, expiresAt: string) =>
        `${projectName} · expires ${expiresAt}`,
      invalidTitle: 'Link unavailable',
      invalidBody:
        "This link has expired, was revoked, or never existed. Ask the project owner for a new one.",
    },
  },

  publicErrors: {
    common: {
      contextLeilao: 'Reverse auction',
      contextCotacao: 'Quotation',
      contextBudgetApproval: 'Budget approval',
      contextResetPassword: 'Reset password',
      contextBuyerPortal: 'Client portal',
      contextOwnerPortal: 'Owner portal',
      contextSupplierPortal: 'Supplier portal',
      contextApprove: 'Quick approval',
      contextSharedMedicao: 'Measurement bulletin',
      contextSharedDre: 'Project results',
      ctaGoHome: 'Go to Builder',
      ctaRequestNewLink: 'Request a new link',
      ctaTryAgain: 'Try again',
      ctaBackToLogin: 'Back to login',
      ctaForgotPassword: 'Request a password link',
      // Wave Codex-R8 · 2026-05-19 · density upgrade
      faqHeading: 'Why did this happen?',
      faqWhyExpired: 'Do these links have an expiry?',
      faqWhyExpiredAnswer:
        'Yes. Builder public links expire to reduce exposure risk if they are forwarded by mistake.',
      faqIsItSafe: 'Is my data still safe?',
      faqIsItSafeAnswer:
        'It is. The content stays on file with the project — only this specific link stopped working.',
      faqStillNeedHelp: 'I still need this access today',
      faqStillNeedHelpAnswer:
        'Whoever sent the link can issue a new one in seconds from the Builder panel.',
      contactSupportLabel: 'Talk to support',
      contactSupportEmail: 'support@builder.nullscience.ai',
      expiryHintPrefix: 'This link stopped working on',
      settingsButtonLabel: 'Preferences',
      settingsPanelTitle: 'Preferences',
      settingsLanguageLabel: 'Language',
      settingsThemeLabel: 'Theme',
      settingsCloseLabel: 'Close preferences',
    },
    leilao: {
      title: "This auction isn't available anymore",
      body:
        'Auction links expire after 7 days or close once the project picks a winner. Ask the project manager for a fresh link to take part again.',
      hint:
        "If you already submitted a bid, your history stays on the supplier panel — the manager can still see what you sent.",
    },
    cotacao: {
      title: "This quotation isn't open anymore",
      body:
        'Quotation links are good for 7 days and close once the project finishes collecting prices. Reach out to the manager to receive a new link.',
      hint:
        "If you answered before the deadline, the project already has your quotes — no need to resend.",
    },
    budgetApproval: {
      title: "This approval link doesn't work anymore",
      body:
        'Budget approval links last 7 days. Ask the project manager to issue a new link and send it again.',
      hint:
        "The original budget is still on file — only the link expired. The manager can resend it any time.",
    },
    resetPassword: {
      title: 'This password link has expired',
      body:
        'For security, password reset links last 30 minutes. Request a new one on the recovery page and try again.',
      hint:
        "If you didn't ask for a reset, you can ignore the email — your password is unchanged.",
    },
    buyerPortal: {
      title: "This access isn't available anymore",
      body:
        'The client portal link was revoked or has expired. Talk to the project manager to receive a new link.',
      hint:
        'Each link is personal — ask the manager to generate one specifically for you.',
    },
    ownerPortal: {
      title: "This access isn't available anymore",
      body:
        'The owner portal link was revoked or has expired. Reach out to the project team to receive a new link.',
      hint:
        'The project data is still protected — only this specific link stopped working.',
    },
    supplierPortal: {
      title: "This link doesn't work anymore",
      body:
        'Supplier links last 7 days for security. To receive a new link, talk to the project manager.',
      hint:
        'If you are the manager and need to issue a new link, open the project panel and go to Suppliers.',
    },
    approve: {
      title: 'This approval link is no longer valid',
      body:
        'Quick approval links (WhatsApp or email) last 7 days and are single-use. Ask whoever sent it for a new link.',
      hint:
        "If you already approved or rejected, the decision was recorded — no need to do it again.",
    },
    sharedMedicao: {
      title: "This measurement isn't available anymore",
      body:
        'Measurement links last for the window set by the manager (usually 7 to 30 days). Ask for a new link if you still need to view it.',
      hint:
        'The measurement is still on file with the project — only this view link expired.',
    },
    sharedDre: {
      title: "This report isn't available anymore",
      body:
        'Profit-and-loss links last for the window set by the manager (usually 7 to 30 days). Ask for a new link if you still need the numbers.',
      hint:
        'The numbers are still on file with the project — only this view link expired.',
    },
  },

  wave27: {
    tabs: {
      curvaAbc: 'ABC curve',
    },
    curva: {
      pageTitle: 'ABC curve',
      subtitle:
        'Items ranked by spend share (Pareto). A = 80%, B = 95%, C = long tail.',
      empty:
        'No purchase orders yet. The ABC curve shows up once the first POs are issued.',
      totalsSpend: 'Total spend',
      totalsItems: 'Unique items',
      totalsPos: 'POs',
      classHeader: 'Class distribution',
      classA: 'A',
      classB: 'B',
      classC: 'C',
      classACaption: 'High-impact — prioritize quotes and contracts',
      classBCaption: 'Medium-impact — review periodically',
      classCCaption: 'Long tail — ad-hoc purchases',
      tableItem: 'Item',
      tableUnit: 'Unit',
      tableQty: 'Qty',
      tableSpend: 'Spend',
      tablePercent: '% of total',
      tableCumulative: 'Cum. %',
      tableSupplier: 'Top supplier',
      tablePos: 'POs',
      generatedAt: (when: string) => `Updated ${when}`,
    },
    catalog: {
      sectionTitle: 'Supplier catalog',
      sectionSubtitle:
        'Reference prices per item to speed up new purchase orders.',
      empty: 'No catalog items yet.',
      addButton: 'New item',
      archiveButton: 'Archive',
      restoreButton: 'Restore',
      nameLabel: 'Name',
      codeLabel: 'Code (optional)',
      unitLabel: 'Unit',
      priceLabel: 'Last price (R$)',
      notesLabel: 'Notes',
      cancel: 'Cancel',
      save: 'Save',
      saveFailed: 'Could not save. Check the fields and try again.',
    },
  },

  wave28: {
    tabs: {
      punchList: 'Handover',
    },
    page: {
      title: 'Delivery punch list',
      subtitle:
        'Final walk-through pending items — what still needs fixing before handover.',
      empty: 'No items yet. Start by recording what the owner flagged on the walk-through.',
      addButton: 'New item',
      readinessLabel: 'Readiness',
      totalLabel: 'Items',
      pendingChip: 'Pending',
      inProgressChip: 'In progress',
      doneChip: 'Done',
      dismissedChip: 'Dismissed',
    },
    columns: {
      pendente: 'Pending',
      em_andamento: 'In progress',
      concluido: 'Done',
      nao_aplicavel: 'Dismissed',
      countSuffix: (n: number) => (n === 1 ? '1 item' : `${n} items`),
    },
    priority: {
      alta: 'High',
      normal: 'Normal',
      baixa: 'Low',
    },
    form: {
      heading: 'New punch list item',
      titleLabel: 'What to fix',
      titlePlaceholder: 'E.g.: Touch up paint on living-room wall',
      descriptionLabel: 'Details (optional)',
      descriptionPlaceholder: 'Notes, measurements, references…',
      locationLabel: 'Location',
      locationPlaceholder: 'E.g.: Master suite',
      priorityLabel: 'Priority',
      cancel: 'Cancel',
      save: 'Add',
      saveFailed: 'Could not save. Try again.',
    },
    actions: {
      start: 'Start',
      finish: 'Complete',
      reopen: 'Reopen',
      dismiss: 'Dismiss',
      restore: 'Restore',
    },
    meta: {
      reportedBy: (name: string, when: string) => `By ${name} · ${when}`,
      location: (loc: string) => `Location: ${loc}`,
      assignedTo: (name: string) => `Owner: ${name}`,
      resolvedAt: (when: string) => `Completed ${when}`,
    },
  },

  wave29: {
    tabs: {
      maoDeObra: 'Labor',
    },
    page: {
      title: 'Daily labor',
      subtitle:
        'Daily headcount by role — masons, helpers, foremen. The base for real cost and labor allocation.',
      empty:
        'No entries yet. Start by logging today’s crew by role.',
      addButton: 'New entry',
      rangeLabel: 'Range',
      rangeAll: 'All',
      rangeLast7: 'Last 7 days',
      rangeLast30: 'Last 30 days',
      rangeMonth: 'This month',
    },
    kpi: {
      totalHours: 'Total hours',
      totalCost: 'Total cost',
      personDays: 'Person-days',
      entries: 'Entries',
      hoursUnit: (n: number) => (n === 1 ? '1 hour' : `${n} hours`),
      personDaysUnit: (n: number) =>
        n === 1 ? '1 person-day' : `${n} person-days`,
      entriesUnit: (n: number) => (n === 1 ? '1 entry' : `${n} entries`),
    },
    roles: {
      pedreiro: 'Mason',
      servente: 'Helper',
      carpinteiro: 'Carpenter',
      armador: 'Rebar worker',
      eletricista: 'Electrician',
      encanador: 'Plumber',
      pintor: 'Painter',
      gesseiro: 'Drywaller',
      azulejista: 'Tiler',
      mestre_obra: 'Site foreman',
      encarregado: 'Supervisor',
      outro: 'Other',
    },
    rollup: {
      heading: 'Breakdown by role',
      roleCol: 'Role',
      personDaysCol: 'Person-days',
      hoursCol: 'Hours',
      costCol: 'Cost',
      shareCol: '% of cost',
      nothingYet: 'No data in the selected range.',
    },
    entries: {
      heading: 'Entries',
      dateCol: 'Date',
      roleCol: 'Role',
      headcountCol: 'Crew',
      hoursPerPersonCol: 'h/person',
      rateCol: '$/h',
      totalCol: 'Total',
      notesCol: 'Notes',
      actionsCol: '',
      edit: 'Edit',
      delete: 'Delete',
      deleteConfirm: (date: string, role: string) =>
        `Delete ${role} entry from ${date}?`,
      deleteFailed: 'Could not delete. Try again.',
    },
    form: {
      heading: 'New entry',
      editHeading: 'Edit entry',
      dateLabel: 'Date',
      roleLabel: 'Role',
      headcountLabel: 'Crew size (people)',
      headcountPlaceholder: 'e.g., 4',
      hoursPerPersonLabel: 'Hours per person',
      hoursPerPersonPlaceholder: 'e.g., 8',
      hourlyRateLabel: 'Hourly rate',
      hourlyRateHint: 'Direct cost + charges. Adjust later if needed.',
      notesLabel: 'Notes (optional)',
      notesPlaceholder: 'Work front, subcontractor, etc.',
      previewLine: (hours: string, cost: string) =>
        `${hours} worked · ${cost}`,
      cancel: 'Cancel',
      save: 'Save',
      saveFailed: 'Could not save. Try again.',
    },
    meta: {
      loggedBy: (name: string, when: string) => `By ${name} · ${when}`,
      dateRange: (from: string, to: string) => `${from} — ${to}`,
    },
  },
  wave30: {
    tabs: {
      rnc: 'NCR',
    },
    page: {
      title: 'Non-conformance register',
      subtitle:
        'Anything that departed from spec, code or contract — with severity, owner, and a two-signature workflow (corrector + verifier).',
      empty:
        'No NCRs logged yet. Open the first one when you spot something off.',
      addButton: 'New NCR',
      filterLabel: 'Filter',
      filterAll: 'All',
      filterOpen: 'Open',
      filterAwaitingVerification: 'Awaiting verification',
      filterOverdue: 'Overdue',
    },
    kpi: {
      total: 'Total',
      open: 'Open',
      overdue: 'Overdue',
      conformance: 'Conformance',
      overdueSuffix: (n: number) =>
        n === 1 ? 'item past due' : 'items past due',
    },
    severity: {
      baixa: 'Low',
      media: 'Medium',
      alta: 'High',
      critica: 'Critical',
    },
    severitySelect: {
      baixa: 'Low — does not block progress',
      media: 'Medium — causes delays but solvable',
      alta: 'High — stops a crew',
      critica: 'Critical — stops the site',
    },
    status: {
      aberto: 'Open',
      em_analise: 'Under review',
      corrigido: 'Corrected',
      verificado: 'Verified',
    },
    statusNext: {
      startAnalysis: 'Start review',
      markCorrected: 'Mark corrected',
      verify: 'Verify and close',
      reopen: 'Reopen',
    },
    list: {
      heading: 'Records',
      titleCol: 'Title',
      areaCol: 'Area',
      severityCol: 'Severity',
      statusCol: 'Status',
      dueCol: 'Due',
      ownerCol: 'Owner',
      actionsCol: '',
      edit: 'Edit',
      delete: 'Delete',
      deleteConfirm: (title: string) => `Delete NCR "${title}"?`,
      deleteFailed: 'Could not delete. Try again.',
      noDueDate: '—',
      overdueBadge: 'Overdue',
    },
    form: {
      heading: 'New NCR',
      editHeading: 'Edit NCR',
      titleLabel: 'Title',
      titlePlaceholder: 'e.g., Lobby wall out of plumb',
      descriptionLabel: 'Description (optional)',
      descriptionPlaceholder:
        'What was found, code/spec reference if any.',
      areaLabel: 'Area / location',
      areaPlaceholder: 'e.g., North facade, suite 203, plumbing riser',
      severityLabel: 'Severity',
      dueDateLabel: 'Due date (optional)',
      dueDateHint: 'Target date for the correction.',
      assigneeLabel: 'Assignee',
      noneOption: 'None',
      cancel: 'Cancel',
      save: 'Save',
      saveFailed: 'Could not save. Try again.',
    },
    meta: {
      reportedBy: (name: string, when: string) =>
        `Opened by ${name} · ${when}`,
      resolvedBy: (name: string, when: string) =>
        `Corrected by ${name} · ${when}`,
      verifiedBy: (name: string, when: string) =>
        `Verified by ${name} · ${when}`,
    },
  },
  wave31: {
    tabs: {
      fvs: 'Inspections',
    },
    page: {
      title: 'Service inspection checklists',
      subtitle:
        'Preventive checklist per service package. Inspector fills, verifier signs. Non-conformities become NCRs.',
      empty:
        'No inspections yet. Pick a template or build one inline.',
      addButton: 'New inspection',
      manageTemplates: 'Templates',
    },
    kpi: {
      total: 'Total',
      open: 'Open',
      awaiting: 'Awaiting verification',
      conformance: 'Conformance',
      nonconformingSuffix: (n: number) =>
        n === 1 ? 'with non-conformity' : 'with non-conformity',
    },
    status: {
      aberta: 'Open',
      preenchida: 'Filled',
      verificada: 'Verified',
    },
    statusNext: {
      submit: 'Submit for verification',
      verify: 'Verify and close',
      reopen: 'Reopen',
    },
    response: {
      conforme: 'Pass',
      nao_conforme: 'Fail',
      na: 'N/A',
    },
    list: {
      heading: 'Project inspections',
      templateCol: 'Service / template',
      stageCol: 'Stage',
      statusCol: 'Status',
      scheduledCol: 'Scheduled',
      inspectorCol: 'Inspector',
      verifierCol: 'Verifier',
      actionsCol: '',
      open: 'Open',
      edit: 'Edit',
      delete: 'Delete',
      deleteConfirm: (title: string) => `Delete inspection "${title}"?`,
      deleteFailed: 'Could not delete. Try again.',
      nonconformityChip: 'Has non-conformity',
      notScheduled: '—',
      noStage: '—',
    },
    form: {
      heading: 'New inspection',
      editHeading: 'Edit inspection',
      templateLabel: 'Template',
      templatePlaceholder: 'Choose a template',
      templateHint: 'Or build a one-off checklist below.',
      inlineTemplateLabel: 'Service title',
      inlineTemplateHint:
        'e.g., Column concreting — 3rd floor',
      inlineItemLabel: 'Checklist item',
      addItem: 'Add item',
      removeItem: 'Remove',
      stageLabel: 'Stage (optional)',
      stagePlaceholder: 'Link to a stage',
      noStage: 'No stage',
      scheduledLabel: 'Scheduled date (optional)',
      notesLabel: 'Notes (optional)',
      cancel: 'Cancel',
      save: 'Save',
      saveFailed: 'Could not save. Try again.',
    },
    fill: {
      heading: 'Inspection form',
      instructions:
        'For each item, mark Pass / Fail / N/A. Use the note to capture evidence or reference.',
      itemHeader: (index: number, total: number) =>
        `Item ${index} of ${total}`,
      noteLabel: 'Note',
      notePlaceholder: 'Evidence, reading or reference.',
      save: 'Save draft',
      submit: 'Submit for verification',
      verify: 'Verify and close',
      reopen: 'Reopen',
      lockedNotice:
        'Inspection locked. Reopen to edit responses.',
      verifyGuardSelf:
        'Inspector and verifier must be different people.',
      saveFailed: 'Could not save. Try again.',
    },
    templates: {
      title: 'Inspection templates',
      subtitle:
        'Reusable checklists for recurring service packages.',
      empty: 'No templates yet — create the first one.',
      createTitle: 'New template',
      editTitle: 'Edit template',
      nameLabel: 'Name',
      namePlaceholder: 'e.g., Structural masonry',
      descriptionLabel: 'Description (optional)',
      descriptionPlaceholder: 'When to apply this checklist.',
      itemsLabel: 'Checklist items',
      itemLabelPlaceholder: 'Item text (e.g., Plumb verified)',
      itemDescriptionPlaceholder: 'Helper note (optional)',
      addItem: 'Add item',
      removeItem: 'Remove',
      save: 'Save template',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      deleteConfirm: (name: string) => `Delete template "${name}"?`,
      itemCount: (n: number) => (n === 1 ? '1 item' : `${n} items`),
      saveFailed: 'Could not save. Try again.',
    },
    meta: {
      scheduledFor: (when: string) => `Scheduled for ${when}`,
      inspectorSigned: (name: string, when: string) =>
        `Filled by ${name} · ${when}`,
      verifierSigned: (name: string, when: string) =>
        `Verified by ${name} · ${when}`,
    },
  },
  ajuda: {
    // W123.4 · 2026-05-26 — AjudaContextual drawer labels.
    label: 'Help',
    tela: 'What this screen is',
    proximaAcao: 'What to do next',
    glossario: 'Glossary',
    eyebrow: 'Help',
    title: 'How Builder works',
    subtitle: 'Each module in one line. Tap to expand and read FAQs.',
    searchPlaceholder: 'Search help…',
    noResults: 'No articles match.',
    pickArticle: 'Pick an article on the left',
    pickArticleHint: 'Or use search to find a specific topic.',
    hub: {
      modulesHeader: 'Main modules',
      modulesSubheader: 'The 10 modules you use every week. Click to see what they do.',
      articlesHeader: 'Detailed articles',
      readMoreCta: 'Learn more',
      closeDrawer: 'Close',
      retakeTourCta: 'Retake interactive tour',
      contactHeader: 'Need direct support?',
      contactBody: 'Talk to us on WhatsApp. We reply within 2 hours on weekdays.',
      contactWhatsappCta: 'Open WhatsApp',
      versionLabel: (version) => `Version ${version}`,
      changelogLink: 'See what is new',
      modules: {
        trena: {
          title: 'Trena · smart estimating',
          description:
            'Import photo, PDF or blueprint. AI extracts measurements, prices via the regional reference price table (SINAPI) automatically.',
          faq:
            'Accepted formats: PDF blueprint, photo of printed blueprint, IFC, or Excel.\n' +
            'Reference price coverage: 290 Brazilian codes and regional multiplier for the 27 states.\n' +
            'Wizard stages: Import · Review · Confirm. Always editable before commit.',
        },
        budget: {
          title: 'Project budget',
          description:
            'Hierarchical list up to 3 levels: chapter, sub-chapter, sub-sub-chapter. Inline editing. Excel export.',
          faq:
            '4 project types: low-income residential, mid residential, commercial store, industrial warehouse.\n' +
            'Sub-sub-chapters: organize large projects in 3 levels (e.g. Foundation > Footings > CA-50 Steel).\n' +
            'Bulk import: paste from Excel directly, Builder matches by description.',
        },
        rdo: {
          title: 'Daily log (RDO)',
          description:
            'Daily record with photos, crew present, activities per stage, weather. Exports PDF for client.',
          faq:
            'Local draft autosave: if internet drops, content returns.\n' +
            'Up to 20 photos per RDO, async upload.\n' +
            'Monthly PDF: exports everything with embedded photos for billing.',
        },
        fvs: {
          title: 'Inspections · quality per stage',
          description:
            'Verification checklist per service with evidence photo. Catches problems before they become rework.',
          faq:
            '3 states: Open · Filled · Verified.\n' +
            'Non-conformant item opens a non-conformance automatically.\n' +
            'Template by project type (residential, commercial, industrial) preloaded.',
        },
        compras: {
          title: 'Purchases · order to payment',
          description:
            'Tag a budget item, open quotation with supplier, generate PO. Payment via PIX, slip or transfer.',
          faq:
            'Flow: request > quotation > PO > receipt > payment.\n' +
            'WhatsApp approval for POs above limit (no login required).\n' +
            'Automatic NFe: paste the XML, Builder matches it to the PO.',
        },
        chat: {
          title: 'Chat · instant messages',
          description:
            'Speech bubble at the top. Quick conversation with emoji and presence. 7-day history.',
          faq:
            'When to use: quick question, time alignment, jobsite photo.\n' +
            'Presence: see who is online in real time.\n' +
            'No subject, no formality. For serious matters use Inbox.',
        },
        inbox: {
          title: 'Inbox · formal messages',
          description:
            'Envelope at the top. Tasks, decisions, audit. Has subject, permanent retention, always exportable.',
          faq:
            'When to use: formal approval, technical decision, client communication.\n' +
            'Everything is audit-logged.\n' +
            'Unlike chat: history never expires here.',
        },
        ownerReport: {
          title: 'Owner report · keep client in the loop',
          description:
            'Weekly PDF for client with photos, progress %, and weekly highlights. No costs, no suppliers.',
          faq:
            'Auto-sent every Monday 8am or manual any time.\n' +
            'Content: progress per stage, public photos, milestones.\n' +
            'Client receives via email and opens via tokenized link, no login.',
        },
        hoje: {
          title: 'Today · next steps',
          description:
            'The 3 most important actions for your project right now. Cockpit to start the day without thinking.',
          faq:
            'Builder looks at everything and prioritizes: pending approvals, late RDOs, critical non-conformances.\n' +
            'Cross-project: shows from all projects you touch, ordered by urgency.\n' +
            'Quick access via the Builder logo at the top.',
        },
        comando: {
          title: 'Command · admin cross-project view',
          description:
            'Operational kanban with every project in your portfolio. Filter by status, alert, deadline.',
          faq:
            'Admin-only. PMs use Today.\n' +
            'Consolidates ~41 admin surfaces in a single screen, direct action without navigation.\n' +
            'Drag-and-drop to change priority or status.',
        },
      },
    },
    fiveCards: {
      header: 'How Builder works in 2 minutes',
      subheader:
        'The 5 core concepts. Learn these, the rest follows by deduction.',
      cta: 'Take me there',
      ctaAria: (title) => `Open ${title}`,
      cards: {
        obra: {
          title: 'Project',
          concept: 'Your physical project and its data.',
        },
        equipe: {
          title: 'Team',
          concept: 'Who works on the project.',
        },
        trena: {
          title: 'Estimate',
          concept: 'Measuring + budgeting is now a wizard.',
        },
        compras: {
          title: 'Purchases',
          concept: 'Request, quote, buy.',
        },
        qualidade: {
          title: 'Quality',
          concept: 'Inspections and non-conformances keep quality on track.',
        },
      },
    },
  },
  wave33: {
    page: {
      title: 'Approve signups',
      subtitle:
        'New accounts are held until an admin or PM approves them and assigns at least one project.',
      empty: 'No pending signups right now.',
      errorLoading: 'Could not load the queue.',
      pendingChip: (count: number) =>
        count === 1 ? '1 pending' : `${count} pending`,
    },
    columns: {
      user: 'User',
      signedUp: 'Signed up',
      via: 'Via',
      actions: '',
    },
    via: {
      google: 'Google',
      email: 'Email',
    },
    approve: {
      button: 'Approve',
      dialogTitle: (name: string) => `Approve ${name}`,
      projectsLabel: 'Assign to projects',
      projectsHint:
        'At least one project. You can add as many as you need — same project twice no, but different roles in different projects yes.',
      pickProject: 'Pick a project…',
      scopeHint:
        'You can only assign the user to projects you also belong to.',
      role: 'Role',
      addAnother: '+ Add another project',
      remove: 'Remove',
      cancel: 'Cancel',
      confirm: 'Approve and assign',
      saving: 'Saving…',
      saveFailed: 'Could not approve. Try again.',
      outOfScope:
        'That project is out of your scope — ask an admin to approve.',
    },
    reject: {
      button: 'Reject',
      confirm: (name: string) =>
        `Reject ${name}? Their access stays blocked.`,
      saveFailed: 'Could not reject. Try again.',
    },
    roles: {
      project_manager: 'Project manager',
      foreman: 'Foreman',
      worker: 'Worker',
      client: 'Client',
      broker: 'Supplier', // W152 · unified from "Broker" → "Supplier"
      admin: 'Admin',
    },
    login: {
      pendingTitle: 'Account awaiting approval',
      pendingBody:
        'Your signup was received. An admin or project manager will approve access shortly.',
    },
    register: {
      pendingTitle: 'Signup received!',
      pendingBody:
        'Your account is awaiting approval. An admin or project manager will review it, and you will get an email as soon as it is approved. No need to sign up again.',
    },
  },
  wave32: {
    nav: {
      executivo: 'Executive',
    },
    page: {
      title: 'Executive report',
      subtitle: 'Cross-project financial close, quality and risk radar',
      generatedAt: (when: string) => `Updated ${when}`,
      fromTo: (from: string, to: string) => `${from} to ${to}`,
      financialsRedacted:
        'Financial indicators hidden — your role is read-only.',
      empty: 'No accessible projects to build the report from.',
      errorLoading: 'Could not load the report.',
    },
    range: {
      last3: 'Last 3 months',
      last6: 'Last 6 months',
      last12: 'Last 12 months',
      ytd: 'Year to date',
    },
    kpi: {
      projects: 'Projects',
      inProgress: 'In progress',
      budget: 'Budget',
      committed: 'Committed',
      paid: 'Paid',
      pending: 'Pending',
      receivables: 'Receivables',
      rncOpen: 'Open non-conformances',
      fvsConformance: 'Inspection conformance',
      laborCost: 'Labor cost',
      manDays: 'man-days',
    },
    burn: {
      heading: 'Burn curve',
      subtitle: 'Settled payments + labor by month',
      paid: 'Payments',
      labor: 'Labor',
      total: 'Total in period',
      empty: 'No activity in the period.',
    },
    suppliers: {
      heading: 'Supplier concentration',
      subtitle: 'Top 10 — issued + received POs',
      col: {
        supplier: 'Supplier',
        pos: 'POs',
        total: 'Committed',
        share: '% of top 10',
      },
      empty: 'No POs in the period.',
    },
    risk: {
      heading: 'Risk radar',
      subtitle: 'At-risk projects first, then by commitment',
      col: {
        project: 'Project',
        status: 'Status',
        budget: 'Budget',
        committed: 'Committed',
        overBudget: 'Variance',
        schedule: 'Schedule',
        rnc: 'Non-conformances',
        issues: 'Issues',
      },
      atRiskChip: 'At risk',
      onTrack: 'OK',
      noBudget: 'No budget',
      noSchedule: '—',
      scheduleAhead: (points: number) => `${points} pts ahead`,
      scheduleBehind: (points: number) => `${points} pts behind`,
      openProject: 'Open project',
    },
  },
  evidenceViewer: {
    openViewer:       'View evidence in document',
    close:            'Close viewer',
    pageOf:           'Page {page} of {total}',
    prevPage:         'Previous page',
    nextPage:         'Next page',
    prevEvidence:     'Previous evidence',
    nextEvidence:     'Next evidence',
    loadingDoc:       'Loading document…',
    errorLoadingDoc:  'Could not load document.',
    noDocStored:      'Original document not available (extracted before Wave 392).',
    ifcNoElement:     'Could not locate this element in the 3D model.',
    ifcFallbackTitle: 'Quantities extracted from this model',
    citationLabel:    'Cited excerpt',
    pdfAriaLabel:     'PDF document · page {page} of {total}',
    ifcAriaLabel:     '3D IFC model',
    legacyBannerTitle:       'Legacy evidence detected',
    legacyBannerCountOne:    '1 item uses evidence captured before the viewer existed.',
    legacyBannerCountOther:  (count: number) => `${count} items use evidence captured before the viewer existed.`,
    legacyBannerSubtitle:    'Re-upload the source document to enable "View in document" on these items.',
    legacyBannerActionLabel: 'Re-upload document',
    panelEmpty:              'Select a measurement with evidence to view the document here.',
  },
  /** Slice 4 · Ready-to-commit yellow row acknowledgements (en). */
  readyToCommit: {
    yellowAckSectionTitle: 'Yellow items · confirmation required',
    yellowAckCheckboxLabel: 'I confirm I have reviewed this yellow item',
    yellowAckHelp: 'Each yellow item requires confirmation before sending to budget. Red blockers still prevent commit.',
  },
  wave43: {
    list: {
      title: 'Client Portal',
      subtitle: 'Houses published to the public sales site.',
      new: 'New portal',
      empty: 'No portals yet.',
      errorLoading: 'Could not load portals.',
      status: {
        draft: 'Draft',
        published: 'Published',
        sold: 'Sold',
        archived: 'Archived',
      },
      columns: {
        title: 'Title',
        slug: 'URL',
        status: 'Status',
        location: 'Location',
        price: 'Price',
        updated: 'Updated',
      },
      openPublic: 'Open site',
    },
    create: {
      title: 'New portal',
      subtitle: 'Pick an existing project. Other fields are edited after creation.',
      projectLabel: 'Project',
      projectHint: 'The project must have a slug.',
      submit: 'Create portal',
      cancel: 'Cancel',
      error: 'Could not create the portal.',
    },
    details: {
      backToList: 'Client Portal',
      openPublic: 'Open public site',
      openPublicDisabled: 'Publish to open site',
      tabs: {
        general: 'General',
        photos: 'Photos',
        documents: 'Documents',
        leads: 'Interested parties',
      },
    },
    general: {
      marketingHeading: 'Marketing',
      marketingHint: 'Appears at the top of the public page.',
      locationHeading: 'Location',
      featuresHeading: 'Features',
      statusHeading: 'Publishing',
      seoHeading: 'SEO',
      fields: {
        heroEyebrow: 'Highlight line (above the title)',
        heroHeadline: 'Main title',
        heroSubheadline: 'Supporting line',
        neighborhood: 'Neighborhood',
        city: 'City',
        state: 'State',
        bedrooms: 'Bedrooms',
        bathrooms: 'Bathrooms',
        parkingSpots: 'Parking',
        areaSqm: 'Built area (m²)',
        lotSqm: 'Lot (m²)',
        amenities: 'Also includes',
        amenitiesHint: 'Comma-separated (e.g. Pool, Fireplace, Deck)',
        listingPrice: 'Listing price (BRL)',
        estimatedDelivery: 'Estimated delivery (YYYY-MM)',
        status: 'Status',
        seoTitle: 'SEO title',
        seoDescription: 'Meta description',
      },
      save: 'Save changes',
      saving: 'Saving…',
      saved: 'Saved.',
      saveFailed: 'Could not save.',
    },
    photos: {
      heading: 'Published photos',
      subtitle: 'Pick which RDO photos appear on the Client Portal.',
      empty: 'No photos on this project yet. Add them via the RDO.',
      publishedChip: 'Published',
      publishBtn: 'Publish',
      unpublishBtn: 'Unpublish',
      uploadedOn: (when: string) => `Captured on ${when}`,
    },
    documents: {
      heading: 'Document package',
      subtitle: 'Token-gated for approved interested parties, buyers, and delivery.',
      checklist: {
        mandatoryMissing: (n: number) => `${n} mandatory missing`,
        recommendedMissing: (n: number) => `${n} recommended missing`,
        complete: 'Checklist complete',
      },
      empty: 'No documents added yet.',
      deleteConfirm: (title: string) => `Remove "${title}"? Buyers lose access.`,
      deleteBtn: 'Remove',
      tier: {
        mandatory: 'Mandatory',
        recommended: 'Recommended',
        optional: 'Optional',
      },
      access: {
        public: 'Public',
        prospect: 'Approved interested party',
        buyer: 'Buyer',
      },
    },
    leads: {
      heading: 'Interested parties',
      subtitle: 'Contacts from the public form. Approve to mint an access link.',
      pendingChip: (n: number) => `${n} pending`,
      showAll: 'Show all',
      showPending: 'Pending only',
      empty: 'No interested parties yet.',
      columns: {
        name: 'Name',
        contact: 'Contact',
        message: 'Message',
        created: 'Received',
        status: 'Status',
      },
      status: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        expired: 'Expired',
      },
      approveBtn: 'Approve',
      rejectBtn: 'Reject',
      rejectConfirm: (name: string) => `Reject contact from ${name}? They won't get access.`,
      approveDialog: {
        title: (name: string) => `Approve ${name}`,
        subtitle: 'Mint a time-limited access link to the private documents.',
        ttlLabel: 'Expires in',
        ttlDaysOption: (n: number) => `${n} day${n === 1 ? '' : 's'}`,
        labelLabel: 'Internal label',
        labelPlaceholder: 'Optional — to remember who',
        submit: 'Mint link',
        cancel: 'Cancel',
        tokenLabel: 'Access link',
        tokenHint: 'Copy and send to the interested party — it expires on the selected date.',
        copyBtn: 'Copy link',
      },
    },
  },
  broker: {
    dashboardTitle: 'Supplier Dashboard',
    openInvitations: 'Open invitations',
    activeBids: 'Active bids',
    winRate: 'Win rate',
    history: 'History',
    noInvitations: 'No invitations found.',
    noBids: 'No bids placed yet.',
    noHistory: 'No completed rounds in history.',
    viewRound: 'View round',
    placeBid: 'Place bid',
    outcome: {
      won: 'Won',
      lost: 'Lost',
      cancelled: 'Cancelled',
    },
    myBest: 'My best price',
    marketBest: 'Market best',
    delta: 'Delta',
    rank: 'Rank',
    totalWon: 'Total won',
    wonRounds: 'Won rounds',
    totalRounds: 'Total rounds',
    columns: {
      round: 'Round',
      project: 'Project',
      status: 'Status',
      outcome: 'Outcome',
      myBid: 'My bid',
      winnerBid: 'Winner',
      delta: 'Δ',
      date: 'Closed at',
    },
  },
  trenaEditAudit: {
    sectionTitle: 'Edit history',
    loading: 'Loading history…',
    error: 'Failed to load history.',
    empty: 'No edits recorded.',
    colWhen: 'When',
    colEditor: 'Editor',
    colChange: 'Before → After',
    colImpact: 'Δ Cost',
    colReason: 'Reason',
    toggleAriaLabel: 'Edit history',
    toggleLabel: 'History',
  },
  adminTrenaStorage: {
    title: 'Trena Storage · R2 Documents',
    objects: 'Documents',
    totalSize: 'Total size',
    oldest: 'Oldest',
    newest: 'Newest',
    orphans: '{n} unreferenced orphans',
    truncated: 'Bucket exceeds limit; showing first batch',
    refresh: 'Refresh',
    empty: 'Empty bucket',
  },
  trenaOcrFallback: {
    ocrBadge: 'Document extracted via OCR · values may have lower accuracy',
    ocrProgress: (current: number, total: number) => `Extracting via OCR · page ${current} of ${total}…`,
  },
  compositionsCompatEdit: {
    editAriaLabel: 'Edit compatibility keys',
    editFormTitle: 'Edit compatibility',
    requiredKeysLabel: 'Required keys',
    blockedKeysLabel: 'Blocked keys',
    addKeyPlaceholder: 'Search measurement key…',
    reasonLabel: 'Reason for edit',
    reasonPlaceholder: 'Optional — describe the reason for this change…',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving…',
    saveError: 'Failed to save. Please try again.',
    invalidKeyError: (keys: string[]) =>
      `Invalid key(s): ${keys.join(', ')}. Only keys present in MEASURE_POLICIES are allowed.`,
    auditTitle: 'Change history',
    auditLoading: 'Loading history…',
    auditError: 'Failed to load history.',
    auditEmpty: 'No changes recorded.',
    auditRowSummary: (added: number, removed: number) =>
      `${added} added, ${removed} removed`,
    auditRowReason: 'Reason:',
    auditToggle: 'History',
  },
  trenaRowActions: {
    triggerAriaLabel: 'Row actions',
    reject: 'Reject',
    restore: 'Restore',
    promoteToAssumption: 'Promote to assumption',
    promoteToMeasurement: 'Promote to measurement',
    viewHistory: 'View history',
    rejectFormTitle: 'Rejection reason',
    rejectReasonPlaceholder: 'E.g. value inconsistent with field survey…',
    rejectReasonHint: (len: number) => `${len} / 5 min.`,
    rejectConfirm: 'Confirm rejection',
    rejecting: 'Rejecting…',
    restoring: 'Restoring…',
    cancel: 'Cancel',
    rejectedBadge: 'Rejected',
    promoteDialogTitleMeasToAssump: 'Promote measurement → assumption',
    promoteDialogTitleAssumpToMeas: 'Promote assumption → measurement',
    promoteDialogDescMeasToAssump:
      'The measurement will be removed and inserted as an assumption with the same value. The operation is atomic.',
    promoteDialogDescAssumpToMeas:
      'The assumption will be removed and inserted as a measurement with the value and unit you provide.',
    promoteValueLabel: 'Value *',
    promoteUnitLabel: 'Unit *',
    promoteScopeLabel: 'Scope *',
    promoteReasonLabel: 'Reason',
    promoteReasonOptional: '(optional)',
    promoteReasonPlaceholder: 'E.g. value is a contractual assumption, not a field measurement…',
    promoteConfirm: 'Confirm promotion →',
    promoting: 'Promoting…',
    promoteError: 'Error promoting.',
    rejectError: 'Error rejecting.',
    scopeProjeto: 'Project (global scope)',
    scopeAmbiente: 'Environment (room-scoped)',
    scopeExterna: 'External (external reference)',
  },
  projectTrenaOverview: {
    title: 'Trena · Estimates',
    newEstimate: 'New estimate',
    filterStatus: 'Status',
    statusAll: 'All',
    statusDraft: 'Draft',
    statusCalculated: 'Calculated',
    statusReviewed: 'Reviewed',
    statusApproved: 'Approved',
    statusExported: 'Exported',
    statusArchived: 'Archived',
    colName: 'Name',
    colStatus: 'Status',
    colDist: 'Distribution',
    colPendingOverrides: 'Pending',
    colTotal: 'Total',
    colLastEdit: 'Last edit',
    colApprovedAt: 'Approved at',
    emptyTitle: 'No estimates in this project.',
    emptyDescription: 'Start a new estimate to calculate the project cost.',
    notCommitted: '—',
    distTooltip: (dist) =>
      `✓ ${dist.safe_to_use} ok · ⚠ ${dist.needs_confirmation} review · ✗ ${dist.blocked} blocked · ✎ ${dist.manually_reviewed} reviewed · ○ ${dist.legacy} legacy`,
    autoRefresh: 'Refreshes every 30s',
    deleteAction: 'Delete',
    deleteAriaLabel: (name: string) => `Delete estimate ${name}`,
  },
  deleteEstimateDialog: {
    title: 'Delete estimate?',
    body: (name: string) => `You are about to delete "${name}".`,
    warning:
      'This action hides the estimate and its items. It cannot be recovered through the interface — contact support if you need to restore it.',
    reasonLabel: 'Reason',
    reasonOptional: '(optional)',
    reasonPlaceholder: 'E.g. duplicate draft, incorrect data…',
    cancel: 'Cancel',
    confirm: 'Delete',
    confirming: 'Deleting…',
  },
  multiDocChecks: {
    duplicateSectionTitle: (count) =>
      `Documents diverge — ${count} measurement${count === 1 ? '' : 's'} with inconsistent values`,
    duplicateSectionDesc:
      'Multiple documents report the same measurement with values that differ beyond tolerance. Verify and resolve before sending to budget.',
    mismatchWarnTitle: (count) =>
      `Inconsistent sources — ${count} discrepanc${count === 1 ? 'y' : 'ies'} between memorial/drawing/IFC`,
    mismatchWarnDesc:
      'Descriptive memorial, drawing, and/or IFC export report different values for the same critical measurement. Confirm which source is correct.',
    mismatchBlockTitle: (count) =>
      `Inconsistent sources — ${count} extreme discrepanc${count === 1 ? 'y' : 'ies'} between sources`,
    mismatchBlockDesc:
      'Extreme divergence (>25%) between memorial, drawing, and/or IFC. Must be resolved before approving the budget.',
    goToConflict: 'Go to consolidation conflict',
    spreadLabel: 'Divergence',
    toleranceLabel: 'Tolerance',
    docCountLabel: 'Docs',
  },
  obraHoje: {
    title: 'Today\'s Queue',
    subtitle: 'Project operating queue',
    updatedAt: (time) => `Updated at ${time}`,
    refresh: 'Refresh',
    nextActionHeading: 'Recommended next action',
    nextActionCta: 'Resolve now',
    bucketApproveToday: 'Approve today',
    bucketMustBuy: 'Must buy',
    bucketMustReceive: 'Receive',
    bucketMustPay: 'Pay',
    bucketLate: 'Late',
    bucketBlocked: 'Blocked',
    bucketChanged: 'Changed today',
    bucketBackground: 'Watching',
    bucketOpenIssues: 'Open issues',
    bucketBudgetApproval: 'Budget awaiting approval',
    seeAll: (n) => `See all · ${n}`,
    emptyBucket: 'No items in this category',
    severityCritical: 'Critical',
    severityWarn: 'Warning',
    severityInfo: 'Info',
    allClearTitle: 'All clear!',
    allClearSubtitle: 'Come back tomorrow to check the next actions.',
    notStartedTitle: 'Your project is ready to start',
    notStartedSubtitle:
      'Kick off the project so Builder starts tracking daily logs, stages, and purchases. Until then, no overdue alerts.',
    notStartedCtaStart: 'Start project',
    notStartedCtaStages: 'Define first stage',
    completedTitle: 'Project completed',
    completedSubtitle: 'History stays available, but the operating queue stays quiet.',
    archivedTitle: 'Project archived',
    archivedSubtitle: 'Reactivate from settings to start receiving alerts again.',
    startConfirmTitle: 'Start the project now?',
    startConfirmBody:
      'Builder will stamp today as the start date, set status to "in progress", and begin monitoring daily logs and deadlines.',
    startConfirmConfirm: 'Yes, kick it off',
    startConfirmCancel: 'Not yet',
    loading: 'Loading project queue…',
    errorLoading: 'Could not load the project queue.',
    deleteDemoCta: 'Delete sample project',
    deleteDemoConfirmTitle: 'Delete the sample project?',
    deleteDemoConfirmBody:
      'We will remove this fictitious project and everything inside it (daily logs, ' +
      'purchases, budget, NCRs). No real project is affected. You can seed it again ' +
      'from the onboarding later.',
    deleteDemoConfirmConfirm: 'Yes, delete',
    deleteDemoConfirmCancel: 'Cancel',
    deleteDemoLoading: 'Deleting sample project…',
    deleteDemoError: "Couldn't delete right now. Try again.",
  },
  obraCockpit: {
    blockHoje: 'Now',
    blockComprar: 'Buy',
    blockReceber: 'Receive',
    blockPagar: 'Pay',
    blockTravado: 'Blocked',
    blockProxima7d: 'Next 7 days',
    hojeSubtitle: (n) => n === 1 ? '1 urgent action' : `${n} urgent actions`,
    comprarSubtitle: (n) => n === 1 ? '1 item without PO' : `${n} items without PO`,
    receberSubtitle: (n) => n === 1 ? '1 pending delivery' : `${n} pending deliveries`,
    pagarSubtitle: (n) => n === 1 ? '1 payment due' : `${n} payments due`,
    travadoSubtitle: (n) => n === 1 ? '1 blocked item' : `${n} blocked items`,
    proxima7dSubtitle: (n) => n === 1 ? '1 scheduled item' : `${n} scheduled items`,
    hojeEmpty: 'No urgent actions right now.',
    proxima7dEmpty: 'Nothing scheduled for this week.',
    allClearTitle: 'Project on track',
    allClearDescription: 'No pending actions for you right now. Your likely next step is to file today\'s daily log.',
    allClearCta: 'Create today\'s daily log',
    feedsInto: 'feeds into schedule and reports',
    errorTitle: 'Could not load the project cockpit.',
    errorDescription: 'Check your internet connection and try again.',
    retry: 'Try again',
    refreshLabel: 'Refresh cockpit',
    loadingLabel: 'Loading cockpit',
  },
  trenaQtyPendencies: {
    bannerTitle: (count) => `Quantity issues (${count})`,
    removeAll: 'Remove all without quantity',
    bannerDesc:
      'Fill in the quantity or remove the item. Calculate will be available once all are resolved.',
    calcularBlockedTooltip: (count) =>
      `Resolve ${count} quantity ${count === 1 ? 'issue' : 'issues'} first.`,
    calcularBadge: (count) =>
      `Calculate (${count} ${count === 1 ? 'issue' : 'issues'})`,
    pendencyBadge: (count) =>
      `${count} quantity ${count === 1 ? 'issue' : 'issues'}`,
    removeItem: 'Remove',
    skippedNotice: (count) =>
      `${count} ${count === 1 ? 'item was skipped' : 'items were skipped'} due to missing quantity.`,
  },
  trenaWizard: {
    stage1Label: 'Import',
    stage2Label: 'Review & Calculate',
    stage3Label: 'Confirm',
    saveDraft: 'Save draft',
    cancel: 'Cancel',
    saveDraftAction: 'Save as draft',
    deleteAction: 'Delete permanently',
    backToWizard: 'Back to wizard',
    advanceToConfirm: 'Advance to confirm →',
    stage1Title: 'Estimate name',
    stage3Title: 'Ready to send to budget?',
    stage3SendCta: 'Send to budget',
    stage3GoBack: '← Back and adjust',
    doneTitle: 'Budget updated!',
    doneTotalLabel: 'Estimated total',
    doneViewBudget: 'View in Budget →',
    doneCreateAnother: 'Create another estimate',
    deleteSuccessToast: 'Estimate deleted',
  },
  trenaPmQuestions: {
    panelTitle: 'Questions for you to answer',
    pendingHeader: 'Pending — needs an answer',
    answeredHeader: 'Answered',
    didNotUnderstandHeader: 'Sections I could not understand',
    emptyState: 'All answered! Ready to calculate the budget.',
    saveButton: 'Save answer',
    skipButton: 'Skip (use default)',
    changeAnswer: 'Change answer',
    dismissDidNotUnderstand: 'Handled it manually, dismiss',
    reExtractButton: 'Re-extract with my answers',
    reExtractRunning: 'Re-extracting…',
    reExtractError: 'Re-extract failed. Please try again.',
    reExtractSuccess: 'Re-extraction finished. Review the results.',
    reExtractNoSourceDoc: 'Could not find the original PDF. Upload it again — your answers will be applied automatically.',
    blockedCalcMessage: 'Answer the questions above before continuing.',
    unblockableMessage: (count: number) =>
      `${count} question${count === 1 ? '' : 's'} will be answered with the default value — may reduce budget accuracy.`,
    otherOption: 'Other (type)',
    otherPlaceholder: 'Your answer',
    saving: 'Saving…',
    loading: 'Loading questions…',
    loadError: 'We could not load the questions.',
    catSistemaConstrutivo: 'Construction system',
    catAcabamento: 'Finish',
    catEstrutura: 'Structure',
    catInstalacao: 'Installation',
    catAmbiente: 'Room',
    catOutro: 'Other',
    kindTabelaComplexa: 'Complex table',
    kindAmbiguidade: 'Ambiguity',
    kindIlegivel: 'Illegible',
    kindForaDeEscopo: 'Out of scope',
    kindValorSuspeito: 'Suspect value',
    kindOutro: 'Other',
    fillDataButton: 'Fill data',
    fillDataPlaceholder: 'Describe the data in free text (e.g. 12 steps, 1.20m wide, granite floor, stainless steel handrail)',
    fillDataSave: 'Save answer',
    fillDataCancel: 'Cancel',
    fillDataSaved: 'Answered at',
    fillDataChangeAnswer: 'Change answer',
    fillDataSavedShort: '✓ Answer saved',
    saveError: 'Failed to save. Please try again.',
    savedFlash: 'Saved',
    customizarPorAmbiente: (count: number) => `Customize per room (${count})`,
    customizarPorAmbienteHeader: 'Customize per room',
    emptyStateCta: 'View detailed budget',
  },
  comprasExcelImport: {
    importBtn: 'Import Excel',
    exportBtn: 'Export Excel',
    pageTitle: 'Import Purchases spreadsheet',
    uploadStep: '1. Upload',
    mappingStep: '2. Columns',
    diffStep: '3. Review',
    confirmStep: '4. Confirm',
    dropzone: 'Drop your .xlsx file here',
    or: 'or',
    browse: 'browse files',
    acceptedFormats: 'Accepts .xlsx (Excel 2007+)',
    analyzing: 'Analyzing spreadsheet…',
    columnHeader: 'Spreadsheet column',
    fieldLabel: 'Purchase field',
    confidence: 'Confidence',
    ignore: 'Ignore',
    addsTitle: (n: number) => `Add ${n} row(s)`,
    updatesTitle: (n: number) => `Update ${n} row(s)`,
    conflictsTitle: (n: number) => `${n} conflict(s)`,
    conflictNote: 'These rows will not be imported — the existing total differs from the calculated one. Resolve manually.',
    applyBtn: (n: number) => `Apply ${n} change(s)`,
    applying: 'Applying…',
    successToast: (adds: number, updates: number) =>
      `Purchases updated: +${adds} added, ${updates} updated.`,
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    noChanges: 'No changes detected. The file is in sync with the current purchases.',
    previewRows: 'Preview (first 20 rows)',
    sheetInfo: (sheet: string, rows: number) => `Sheet "${sheet}" · ${rows} data row(s)`,
    mappingHint: 'Review column mapping. Use "Ignore" to skip a column.',
    warnings: 'Warnings',
  },
  cronogramaExcelImport: {
    importBtn: 'Import Excel',
    exportBtn: 'Export Excel',
    pageTitle: 'Import Schedule spreadsheet',
    uploadStep: '1. Upload',
    mappingStep: '2. Columns',
    diffStep: '3. Review',
    confirmStep: '4. Confirm',
    dropzone: 'Drag the .xlsx file here',
    or: 'or',
    browse: 'select from computer',
    acceptedFormats: 'Accepts .xlsx (Excel 2007+)',
    analyzing: 'Analysing spreadsheet…',
    columnHeader: 'Spreadsheet column',
    fieldLabel: 'Schedule field',
    confidence: 'Confidence',
    ignore: 'Ignore',
    addsTitle: (n: number) => `Add ${n} stage(s)`,
    updatesTitle: (n: number) => `Update ${n} stage(s)`,
    conflictsTitle: (n: number) => `${n} conflict(s)`,
    conflictNote: 'These stages will not be imported — status conflict. Resolve manually.',
    applyBtn: (n: number) => `Apply ${n} change(s)`,
    applying: 'Applying…',
    successToast: (adds: number, updates: number) =>
      `Schedule updated: +${adds} added, ${updates} updated.`,
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    noChanges: 'No changes detected. The file is in sync with the current schedule.',
    previewRows: 'Preview (first 20 rows)',
    sheetInfo: (sheet: string, rows: number) => `Sheet "${sheet}" · ${rows} data row(s)`,
    mappingHint: 'Review column mapping. Use "Ignore" to skip a column.',
    warnings: 'Warnings',
  },
  fornecedoresExcelImport: {
    importBtn: 'Import Excel',
    exportBtn: 'Export Excel',
    pageTitle: 'Import Suppliers spreadsheet',
    uploadStep: '1. Upload',
    mappingStep: '2. Columns',
    diffStep: '3. Review',
    confirmStep: '4. Confirm',
    dropzone: 'Drag the .xlsx file here',
    or: 'or',
    browse: 'select from computer',
    acceptedFormats: 'Accepts .xlsx (Excel 2007+)',
    analyzing: 'Analysing spreadsheet…',
    columnHeader: 'Spreadsheet column',
    fieldLabel: 'Supplier field',
    confidence: 'Confidence',
    ignore: 'Ignore',
    addsTitle: (n: number) => `Add ${n} supplier(s)`,
    updatesTitle: (n: number) => `Update ${n} supplier(s)`,
    conflictsTitle: (n: number) => `${n} conflict(s)`,
    conflictNote: 'These suppliers will not be imported — CNPJ already registered under a different name. Edit manually.',
    applyBtn: (n: number) => `Apply ${n} change(s)`,
    applying: 'Applying…',
    successToast: (adds: number, updates: number) =>
      `Suppliers updated: +${adds} added, ${updates} updated.`,
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    noChanges: 'No changes detected. The file is in sync with the current suppliers.',
    previewRows: 'Preview (first 20 rows)',
    sheetInfo: (sheet: string, rows: number) => `Sheet "${sheet}" · ${rows} data row(s)`,
    mappingHint: 'Review column mapping. Use "Ignore" to skip a column.',
    warnings: 'Warnings',
  },
  medicoesExcelImport: {
    importBtn: 'Import Excel',
    exportBtn: 'Export Excel',
    pageTitle: 'Import Measurements spreadsheet',
    uploadStep: '1. Upload',
    mappingStep: '2. Columns',
    diffStep: '3. Review',
    confirmStep: '4. Confirm',
    dropzone: 'Drag the .xlsx file here',
    or: 'or',
    browse: 'select from computer',
    acceptedFormats: 'Accepts .xlsx (Excel 2007+)',
    analyzing: 'Analysing spreadsheet…',
    columnHeader: 'Spreadsheet column',
    fieldLabel: 'Measurement field',
    confidence: 'Confidence',
    ignore: 'Ignore',
    addsTitle: (n: number) => `Add ${n} measurement(s)`,
    updatesTitle: (n: number) => `Update ${n} measurement(s)`,
    conflictsTitle: (n: number) => `${n} conflict(s)`,
    conflictNote: 'These measurements will not be imported — status prevents overwrite. Resolve manually.',
    applyBtn: (n: number) => `Apply ${n} change(s)`,
    applying: 'Applying…',
    successToast: (adds: number, updates: number) =>
      `Measurements updated: +${adds} added, ${updates} updated.`,
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    noChanges: 'No changes detected. The file is in sync with the current measurements.',
    previewRows: 'Preview (first 20 rows)',
    sheetInfo: (sheet: string, rows: number) => `Sheet "${sheet}" · ${rows} data row(s)`,
    mappingHint: 'Review column mapping. Use "Ignore" to skip a column.',
    warnings: 'Warnings',
  },
  excelImportShell: {
    uploadStep: '1. Upload',
    previewStep: '2. Preview',
    diffStep: '3. Diff',
    confirmStep: '4. Confirm',
    dropzone: 'Drop your .xlsx or .csv file here',
    or: 'or',
    browse: 'browse files',
    acceptedFormats: 'Accepts .xlsx (Excel 2007+) or .csv',
    analyzing: 'Analyzing…',
    pasteToggle: 'Paste table from Excel',
    pasteToggleClose: 'Close paste area',
    pasteLabel: 'Paste data copied from Excel or Google Sheets here:',
    pastePlaceholder: 'Column1\tColumn2\tColumn3\nValue1\tValue2\tValue3',
    pasteCta: 'Import pasted data',
    exportCurrentTitle: 'Download current state',
    exportCurrentDesc: 'Export what is in the system now before importing.',
    exportCurrentBtn: 'Download Excel',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Continue',
    applying: 'Applying…',
    applied: 'Applied successfully',
    undoAvailable: (s: number) => `Undo available for ${s}s`,
    undoBtn: 'Undo',
    undoing: 'Undoing…',
    undone: 'Import undone successfully.',
    importAnother: 'Import another file',
    close: 'Close',
    noChanges: 'No changes detected. The file is in sync with the current state.',
    errorsInFile: (n: number) => `${n} parse error(s)`,
    fixFirst: (n: number) => `${n} error(s) — fix first`,
    applyChanges: (n: number) => `Apply ${n} change(s)`,
    added: 'Added',
    updated: 'Updated',
    deleted: 'Deleted',
    skipped: 'Skipped',
    errors: 'Errors',
    parseErrors: (n: number) => `Parse errors (${n})`,
  },
  verbaExcelImport: {
    pageTitle: 'Import Monthly Budget',
    feedsInto: 'feeds into budget and ABC curve',
    importBtn: 'Import Excel',
    exportBtn: 'Export Excel',
  },
  estoqueExcelImport: {
    pageTitle: 'Import Stock',
    feedsInto: 'feeds into supply reports and movements',
    importBtn: 'Import Excel',
    exportBtn: 'Export Excel',
  },
  rdosExcelImport: {
    pageTitle: 'Import historical RDOs',
    feedsInto: 'useful for migrating history from previous projects',
    importBtn: 'Import Excel',
    exportBtn: 'Export Excel',
  },
  pranchasExcelImport: {
    pageTitle: 'Bulk import Drawings',
    feedsInto: 'registers drawings without uploading PDFs',
    importBtn: 'Import Excel',
    exportBtn: 'Export Excel',
  },
  trenaExcelImport: {
    pageTitle: 'Import Trena Measurements',
    feedsInto: 'feeds rooms and estimates in Trena',
    importBtn: 'Import Excel',
    exportBtn: 'Export Excel',
  },
  emptyStatesLlll: {
    compras: {
      title: 'Start with your first order',
      description: 'Create a PO or import your current pipeline.',
      primaryCta: '+ New PO',
      importExcel: 'Import Excel',
      viewExample: 'View example',
    },
    orcamento: {
      title: 'Import your budget or start from scratch',
      description: 'Got an Excel? Import in seconds. No budget yet? Start with a template.',
      primaryCta: '+ Add item',
      importExcel: 'Import Excel',
      templateResidential: 'Residential template',
      templateCommercial: 'Commercial template',
    },
    cronograma: {
      title: 'Plan your project stages',
      description: 'Create stages, milestones and dependencies. Import from Excel or start with a template.',
      primaryCta: '+ New stage',
      templateResidential: 'Residential template',
      importExcel: 'Import Excel',
    },
    fornecedores: {
      title: 'Add your first supplier',
      description: 'Pre-register suppliers or import your current Excel list.',
      primaryCta: '+ Add supplier',
      importExcel: 'Import Excel',
    },
    diarioObra: {
      title: "Start today's diary",
      description: 'Log weather, team, activities and photos. Takes 1 minute.',
      primaryCta: "+ Log today's RDO",
      viewExample: 'View RDO example',
    },
    qualidade: {
      title: 'Track construction quality',
      description: 'Create inspection checklists, log non-conformances and maintain history.',
      primaryCta: '+ New inspection',
      templatesFvs: 'Inspection templates',
      viewGuide: 'View guide',
    },
  },
  inlineTutorials: {
    budget: {
      title: 'Start with the project budget',
      subtitle: 'The budget is the source of truth. Purchases, measurements and finance all pull from here.',
      step1Title: 'Import with Trena',
      step1Description: 'Floor-plan photo, project PDF, or type it in. Trena estimates quantities for you.',
      step2Title: 'Review quantities and prices',
      step2Description: 'Adjust what needs adjusting. You decide what stays and what each item costs.',
      step3Title: 'Approved becomes truth',
      step3Description: 'Purchases pull items, measurements compare progress, finance tracks spend.',
      primaryCta: 'Import with Trena',
      secondaryCta: 'Add item manually',
    },
    rdo: {
      title: 'Log the day on site',
      subtitle: 'The daily log (RDO) documents what happened: weather, crew, activities and photos.',
      step1Title: 'Photograph the site',
      step1Description: 'Work fronts, problems, deliveries. Anything the client asks tomorrow, you answer with a photo.',
      step2Title: 'List who worked',
      step2Description: 'Crew on site and activities of the day. One place, no more WhatsApp threads.',
      step3Title: 'Mark weather and events',
      step3Description: 'Rain, stoppage, accident. Becomes the official record that justifies schedule and billing.',
      primaryCta: "Create today's RDO",
      secondaryCta: 'See an example',
    },
    compras: {
      title: 'Buy what the site needs',
      subtitle: 'Purchases pull from the approved budget. Quote 3+ suppliers, pick the winner, generate the PO.',
      step1Title: 'Flag items to quote',
      step1Description: 'In the budget, flag items that need buying. They show up in the list of items to quote here.',
      step2Title: 'Send the quotation by email',
      step2Description: 'Builder emails suppliers. Each replies with a proposal, no account required.',
      step3Title: 'Compare and issue the PO',
      step3Description: 'See proposals side by side, pick the winner per line. The order is generated automatically.',
      primaryCta: 'Go to budget',
      secondaryCta: 'Register supplier',
    },
    fvs: {
      title: 'Check the quality',
      subtitle: 'The inspection (service-verification sheet) confirms each stage met the standard before moving on.',
      step1Title: 'Pick a template',
      step1Description: 'Structure, masonry, MEP. Each stage has its own checklist of critical points.',
      step2Title: 'Mark verified items',
      step2Description: 'On site, tick what passed and what is pending. Builder calculates conformance.',
      step3Title: 'Photo and signature',
      step3Description: 'Photographic evidence plus sign-off. Ready for the client audit.',
      primaryCta: 'New inspection',
      secondaryCta: 'View templates',
    },
  },
  prumoQueue: {
    bucketApproveToday: 'Approve today',
    bucketBlocked: 'Blocked',
    bucketFyi: 'FYI',
    bucketBackgroundHealth: 'System health',
    fazerAgora: 'Do it now',
    porQueExpand: 'Why this suggestion?',
    porQueCollapse: 'Hide details',
    techDetails: 'View technical details',
    approveBtn: 'Approve',
    declineBtn: 'Decline',
    declineConfirmBtn: 'Confirm decline',
    declineCancelBtn: 'Cancel',
    declineNotePlaceholder: 'Why declined? (optional)',
    resolverBtn: 'Resolve',
  },
  notificationsBell: {
    title: 'Notifications',
    openBell: 'Open notifications',
    empty: 'No notifications yet',
    markAllRead: 'Mark all as read',
    loading: 'Loading…',
    unread: 'Unread',
  },
  pranchas: {
    pageTitle: 'Drawings',
    noDrawings: 'No drawings registered',
    noDrawingsDesc: 'Add a drawing above to get started.',
    filterByDiscipline: 'Filter discipline',
    allDisciplines: 'All disciplines',
    addDrawing: 'Add drawing',
    code: 'Code',
    name: 'Name',
    discipline: 'Discipline',
    revisionLabel: 'Revision',
    revisionLabelPlaceholder: 'V1, R00, Rev A…',
    uploadPdf: 'Select PDF',
    uploadRevision: 'Upload revision',
    uploadingRevision: 'Uploading…',
    revisionUploaded: 'Revision uploaded successfully.',
    noRevision: 'No revision',
    revisionsTimeline: 'Revision history',
    currentRevision: 'Current revision',
    markupTools: 'Markup tools',
    toolBox: 'Rectangle',
    toolCircle: 'Circle',
    toolArrow: 'Arrow',
    toolText: 'Text',
    toolSelect: 'Select',
    markupLabelPlaceholder: 'Annotation…',
    anchorRfi: 'Create RFI here',
    anchorRfiTitle: 'Anchor RFI to this coordinate',
    rfiPinTooltip: 'View RFI',
    rfiDetails: 'RFI details',
    decisionHistory: 'Decision history',
    noAnchors: 'No RFIs anchored to this revision.',
    loadingPdf: 'Loading PDF…',
    pdfError: 'Error loading PDF.',
    pageOf: (current: number, total: number) => `Page ${current} of ${total}`,
    prevPage: 'Previous',
    nextPage: 'Next',
    noMarkups: 'No markups on this page.',
    notes: 'Notes',
    notesPlaceholder: 'Notes about this revision…',
    disciplines: {
      arquitetura: 'Architecture',
      estrutural: 'Structural',
      hidraulica: 'Hydraulic',
      eletrica: 'Electrical',
      gas: 'Gas',
      spda: 'Lightning protection',
      prevenivel: 'Preventive',
      outros: 'Others',
    },
    dwgStub: {
      title: 'Builder does not convert DWG automatically yet',
      description: 'For now, export as PDF from AutoCAD before uploading:',
      steps: [
        'Open the DWG file in AutoCAD',
        'Go to File → Plot (Ctrl+P)',
        'Select the DWG to PDF.pc3 printer',
        'Click OK and save the PDF',
      ],
      howToExportLink: 'https://knowledge.autodesk.com/support/autocad/learn-explore/caas/sfdcarticles/sfdcarticles/How-to-create-a-PDF-from-AutoCAD.html',
      howToExportLabel: 'How to export PDF from AutoCAD',
      confirmBtn: 'Got it, I will export',
    },
  },

  adminHome: {
    pageTitle: 'Admin Panel',
    pageSubtitle: 'Live overview of all admin surfaces.',
    refresh: 'Refresh',
    loading: 'Loading counts…',
    error: 'Failed to load panel. Try again.',
    tiles: {
      overrides: {
        title: 'Trena Overrides',
        descriptor: 'this week',
        zero: '0 this week',
      },
      storage: {
        title: 'R2 Storage',
        descriptor: 'objects',
        zero: 'Bucket empty',
      },
      compositions: {
        title: 'Compositions',
        descriptor: 'total',
        compatSuffix: 'with compat',
        zero: 'No compositions',
      },
      followUps: {
        title: 'Follow-ups',
        descriptor: 'today',
        kindsSuffix: 'kinds',
        zero: 'None today',
      },
      auctions: {
        title: 'Auctions',
        descriptor: 'active',
        zero: 'None active',
      },
      users: {
        title: 'Users',
        descriptor: 'active (7 days)',
        zero: 'None active',
      },
    },
  },

  projectActivity: {
    pageTitle: 'Project activity',
    pageSubtitle: 'Chronological history of everything that happened on this project.',
    widgetTitle: 'Recent activity',
    widgetViewAll: 'View full history',
    loadFailed: 'Could not load the activity history.',
    empty: 'No activity recorded in this period.',
    emptyFilter: 'No activity of this type in the period.',
    loadMore: 'Load more',
    refreshLabel: 'Refresh',
    filterAll: 'All',
    kindLabels: {
      rdo_created: 'Daily log created',
      po_created: 'PO created',
      po_received: 'Receipt recorded',
      estimate_edited: 'Estimate measurement edited',
      estimate_committed: 'Estimate measurement committed',
      fvs_passed: 'Inspection passed',
      fvs_failed: 'Inspection failed',
      rfi_opened: 'RFI opened',
      rfi_answered: 'RFI answered',
      rnc_created: 'Non-conformance opened',
      photo_uploaded: 'Photo uploaded',
      override_committed: 'Change order submitted',
      doc_uploaded: 'Document uploaded',
      stage_completed: 'Stage completed',
      budget_changed: 'Budget changed',
    },
    actorSystem: 'System',
    today: 'Today',
    yesterday: 'Yesterday',
    // Wave merge-activity-audit · 2026-05-21 · Tabs (Project events vs Technical trail).
    tabEvents: 'Project events',
    tabAudit: 'Technical trail',
    tabsAriaLabel: 'History views',
  },

  projectSwitcher: {
    placeholder: 'Select a project',
    triggerAriaLabel: 'Switch project',
    searchPlaceholder: 'Search projects...',
    noResults: 'No projects found.',
    viewAll: 'View all projects',
  },

  // Wave A · Mobile resilience pass.
  mobileResilient: {
    errorTitle: 'Could not load right now',
    errorDescription: 'Check your connection and try again.',
    retryLabel: 'Try again',
    showDetails: 'Show technical details',
    offlineLabel: 'No connection',
    offlineShowingSaved: 'showing saved data',
    offlineReconnecting: 'trying to reconnect',
    timedOutTitle: 'Taking longer than usual',
    timedOutDescription: 'Could be your connection. Want to try again?',
  },

  // Wave B · EmptyState system.
  emptyState: {
    projects: {
      title: 'Create your first project',
      description: 'Centralise daily logs, photos, RDOs, budget and purchases in one place.',
      primaryAction: 'Create project',
      feedsInto: 'the daily RDO, ABC Curve and client reports',
    },
    fvs: {
      title: 'No inspections yet',
      description: 'The inspection is the quality checklist per service. Use ready-made templates (masonry, plumbing, facade).',
      primaryAction: 'Use NBR template',
      secondaryCreate: 'Create from scratch',
      feedsInto: 'the project quality report',
    },
    verba: {
      title: 'Set up project budgets',
      description: 'Define spending limits per category. Budget and purchases will use these limits.',
      primaryAction: 'Configure budget',
      secondaryImport: 'Import spreadsheet',
    },
    estoque: {
      title: 'Stock is empty',
      description: 'Record entries via Purchases or directly. Stock tracks RDO consumption.',
      primaryAction: 'Record entry',
    },
    compras: {
      title: 'No purchases yet',
      description: 'Mark items in the budget, import an order spreadsheet, or create a quick purchase.',
      primaryAction: 'Go to budget',
      secondaryImport: 'Import spreadsheet',
      secondaryQuick: 'Quick purchase',
      feedsInto: 'stock, budget limits and the ABC Curve',
    },
    inbox: {
      title: 'Inbox is empty',
      description: 'When something needs your attention, it will show here first.',
    },
    trena: {
      title: 'No estimates yet',
      description: 'Trena extracts measurements from PDF or IFC. It is the first step before budgeting.',
      primaryAction: 'New estimate',
      feedsInto: 'the project budget and ABC Curve',
    },
    pranchas: {
      title: 'No drawings yet',
      description: 'Upload a PDF drawing sheet. You can add markups and link RFIs.',
      primaryAction: 'Upload drawing',
    },
    photos: {
      title: 'Gallery is empty',
      description: 'Document project progress with photos. They appear in the client report.',
      primaryAction: 'Upload photos',
    },
    cronograma: {
      title: 'Schedule has no stages',
      description: 'Define project stages to track progress and productivity.',
      primaryAction: 'Create stage',
      feedsInto: 'the RUP and client progress report',
    },
    // DP3 · empty-state ruthless audit
    members: {
      title: 'No team members yet',
      description: 'Add the team to log daily reports, stage progress and quality checks.',
      primaryAction: 'Invite member',
      feedsInto: 'the daily log, stages and inspections',
    },
    invitations: {
      title: 'No pending invitations',
      description: 'Sent invitations appear here until the member accepts.',
      primaryAction: 'Invite client',
    },
    notifications: {
      title: 'No notifications',
      description: 'Approval alerts, deadlines and updates appear here.',
      primaryAction: 'Go to inbox',
    },
    ownerStages: {
      title: 'Waiting for project stages',
      description: 'The manager has not set up stages yet. Check back soon.',
    },
    ownerTimeline: {
      title: 'Waiting for updates',
      description: 'The manager publishes milestones and photos here as work progresses.',
    },
    ownerPhotos: {
      title: 'Waiting for site photos',
      description: 'The manager documents progress with photos. Check back in a few days.',
    },
    stockMovements: {
      title: 'No movements recorded',
      description: 'Log entries and exits to see the movement history here.',
    },
    activityByProject: {
      title: 'No recent activity',
      description: 'Daily log events, photos and inspections appear here by project.',
    },
    activityDays: {
      title: 'No activity in this period',
      description: 'Log RDOs, photos or inspections to see the history here.',
      primaryAction: 'Go to daily log',
    },
    approvalsByProject: {
      title: 'No pending items by project',
      description: 'Budget and purchase approvals appear grouped here.',
    },
    laborRollup: {
      title: 'No team data yet',
      description: 'Log attendance to see a summary by role.',
      primaryAction: 'Log attendance',
    },
    savedReports: {
      title: 'No saved reports',
      description: 'Run a report and save it for quick access later.',
    },
    checklists: {
      title: 'No checklists for this stage',
      description: 'Start a service verification checklist for this stage.',
      primaryAction: 'Start checklist',
    },
    quotations: {
      title: 'No quotations yet',
      description: 'Quotations start from a purchase order with marked items.',
      primaryAction: 'New quotation',
      secondaryAction: 'Go to budget',
      feedsInto: 'purchases and the project ABC Curve',
    },
    stageBudgetItems: {
      title: 'No budget items marked',
      description: 'Go to the budget and mark items to generate stages.',
      primaryAction: 'Go to budget',
    },
  },

  // Wave F · Compras onboarding + Settings cleanup
  waveF: {
    comprasOnboarding: {
      heading: "Let's start buying",
      description: "You haven't marked any items to buy yet — that's fine. Pick a path: mark items in the budget, import a spreadsheet, or create a quick purchase.",
      feedsInto: 'Feeds budget · inventory · ABC curve',
      tileOrcamento: 'Go to budget',
      tileOrcamentoSub: 'Mark items as "to buy" and they appear here',
      tileImport: 'Import material order',
      tileImportSub: 'Have a spreadsheet ready? Upload in seconds',
      tileQuickCreate: 'Create quick purchase',
      tileQuickCreateSub: "No need for the budget — just description and price",
      tileRecomendacao: "Week's recommendation",
      tileRecomendacaoSub: 'Top budget items with purchase urgency',
    },
    quickCreate: {
      title: 'Create quick purchase',
      subtitle: 'Creates a PO without a prior quote — useful when you already have the price and supplier.',
      fieldDescription: 'Item description',
      fieldQty: 'Quantity',
      fieldUnit: 'Unit',
      fieldPrice: 'Estimated price (R$)',
      fieldSupplier: 'Supplier',
      noSupplier: 'No supplier defined',
      ctaCreate: 'Create PO',
      errDescription: 'Enter the item description.',
      errQty: 'Invalid quantity.',
      errPrice: 'Invalid estimated price.',
      errGeneric: 'Could not create the purchase.',
    },
    recomendacao: {
      title: "Week's recommendation",
      subtitle: 'Budget items marked for purchase, ordered by value. Select the ones you want to turn into POs now.',
      empty: 'No items marked for purchase in the budget yet.',
      emptySub: 'Go to the budget and mark items as "to buy" to see them here.',
      selected: (n: number, total: number) => `${n} of ${total} selected`,
      ctaCreate: 'Create consolidated PO for selected',
    },
  },

  // Wave E · Trena confidence UX · 2026-05-17
  trenaConfidence: {
    preupload: {
      title: 'What type of file works?',
      dismissBtn: 'Hide tip',
      showBtn: 'Show file tip',
      goodTitle: 'Good file',
      goodDesc: 'Descriptive memo with detailed areas, architectural project with dimensions, BIM IFC file.',
      borderlineTitle: 'Borderline file',
      borderlineDesc: "Low-angle photo of a plan, low-quality scanned PDF. I'll try OCR but confidence will be low.",
      badTitle: 'Bad file',
      badDesc: "Document without dimensions, off-angle photo, spreadsheet without area column. I'll ask you to enter measurements manually.",
    },
    narration: {
      taskComodos: 'Identifying rooms',
      taskAreas: 'Calculating areas',
      taskEsquadrias: 'Identifying doors and windows',
      taskRevestimentos: 'Detecting finishes',
      taskInconsistencias: 'Looking for inconsistencies',
      completeMsg: 'Done! Opening the review.',
    },
    confidencePanel: {
      title: 'Confidence by category',
      clearFilter: 'Show all',
      summaryPrefix: 'Overall confidence:',
      summaryOverall: (label: string) => label,
    },
    checklist: {
      title: (remaining: number) =>
        remaining === 0
          ? 'All items confirmed'
          : `I need you to confirm ${remaining} point${remaining !== 1 ? 's' : ''}`,
      allDoneTitle: 'All items confirmed',
      subtitle: 'Measurements with low confidence or conflict between documents.',
      confirmBtn: 'Confirm',
      correctBtn: 'Fix with evidence',
      reviewItem: 'Review this item',
    },
    budgetCompare: {
      title: 'Comparison with current budget',
      emptyDesc: 'No previous budget to compare.',
      emptySubtitle: 'This is the first Trena estimate for this project.',
      colOld: 'Current budget',
      colNew: 'Trena estimate',
      colDelta: 'Total difference',
      topDiffsTitle: 'Biggest differences by category',
    },
    onboarding: {
      titleChoice: 'How would you like to start?',
      subtitleChoice:
        'Documents give you a tighter estimate. Without them you can still see a preliminary preview.',
      recommendedBadge: 'recommended',
      optionWithDocs: 'I have project documents',
      descriptionWithDocs:
        'Upload memorandum, standard plans, drawings, etc. The system extracts data and proposes an initial estimate for you to review.',
      continueWithDocs: 'Continue with docs',
      optionWithoutDocs: 'I want a quick estimate without documents (preview)',
      descriptionWithoutDocs:
        'Type 5 basic facts and get a preliminary estimate. To approve it or use it in purchases / contracts, you will need to upload documents later.',
      continueWithoutDocs: 'Continue without docs',
      backToChoice: 'Back to choice',
      uploadTitle: 'Upload the main project document',
      uploadSubtitle:
        'Memorandum, architectural drawing or similar. The system extracts measurements, finish standard and region automatically.',
      uploadDropLabel: 'Click or drag PDFs here',
      uploadAcceptHint: 'PDF(s) up to 20MB each · max. 8 files · 50MB total.',
      uploadingFile: 'Processing documents…',
      processingFile: 'Reading {file}…',
      multipleFilesHint: 'You can select multiple PDFs at once.',
      processingFiles: '{done} of {total} file(s) processed',
      tooManyFiles: 'Maximum 8 files at a time.',
      totalSizeExceeded: 'Total size exceeds 50MB. Remove a file or compress.',
      skipDocsHint: 'No documents yet?',
      skipToPreview: 'Estimate in preview mode',
      reviewTitle: 'Review the extracted data',
      reviewSubtitle:
        'Confirm or correct the pre-populated fields. You can edit any value before generating the estimate.',
      extractionSummary: 'Extraction summary',
      changeDoc: 'Change document(s)',
      evidenceFor: 'Evidence',
      evidenceLabel: 'Quote from the document',
      alternativeSource: '+ {n} other(s)',
      viewSources: 'View sources',
    },
    preview: {
      bannerTitle: 'This estimate was generated without documents.',
      bannerBody:
        'You can approve and use it as is — Builder produced the ballpark from your Stage 0 inputs. Upload a memorandum or project plan anytime to tighten confidence.',
      bannerCta: 'Upload documents (optional)',
      bannerInFormTitle: 'No documents',
      bannerInFormBody:
        "Running without docs is fine — the estimate stays as a ballpark. You can upload a memorandum or project plan later to raise confidence.",
      previewBlocksApprovalError:
        'Estimate is in preview mode. Upload a memorandum or project plan before approving.',
    },
    stage0: {
      title: "Let's start with the overview",
      subtitle: 'Five short questions to generate an initial estimate with a confidence band.',
      tipologia: 'Project type',
      tipologiaResidencialUni: 'Residential — single family',
      tipologiaResidencialMulti: 'Residential — multifamily',
      tipologiaComercial: 'Commercial',
      tipologiaIndustrial: 'Industrial',
      areaConstruida: 'Total built area',
      areaPlaceholder: 'e.g. 287',
      padrao: 'Finish standard',
      padraoBaixo: 'Low',
      padraoMedio: 'Medium',
      padraoAlto: 'High',
      padraoLuxo: 'Luxury',
      padraoLuxoExtremo: 'Extreme luxury',
      padraoBaixoHint: '~R$ 2,280/m² (SP, Jan/2025)',
      padraoMedioHint: '~R$ 2,890/m² (SP, Jan/2025)',
      padraoAltoHint: '~R$ 3,650/m² (SP, Jan/2025)',
      padraoLuxoHint: '~R$ 4,720/m² (SP estimated)',
      padraoLuxoExtremoHint: '~R$ 6,850/m² (SP estimated)',
      regiaoUf: 'State (UF)',
      regiaoCidade: 'City (optional)',
      regiaoCidadePlaceholder: 'e.g. São Paulo',
      prazo: 'Expected project duration',
      prazoPlaceholder: 'e.g. 14',
      submit: 'Generate initial estimate →',
      submitting: 'Calculating…',
      errorGeneric: 'Could not generate the estimate. Try again.',
      errorCubNotAvailable: "That region isn't covered by the CUB database yet. For now, use SP.",
    },
    panel: {
      title: 'Estimated budget',
      totalCentral: 'Central estimate',
      band: 'Confidence band 90%',
      uncertaintyBreakdownTitle: 'Where uncertainty comes from',
      uncBreakSistemas: 'Undefined systems',
      uncBreakPadrao: 'Per-room finish standard',
      uncBreakVolatilidade: 'Price volatility',
      uncBreakCub: 'Approximate regional CUB',
      uncBreakIndiretos: 'Undeclared indirects (BDI, payroll taxes, site overhead)',
      uncBreakOutros: 'Other',
      bucketsTitle: 'By bucket',
      bucketEstrutura: 'Structure',
      bucketAlvenaria: 'Masonry',
      bucketAcabamento: 'Finishes',
      bucketInstalacoes: 'Installations',
      bucketExternos: 'External',
      bucketBdiCanteiro: 'BDI + site',
      reduceBandTitle: 'To narrow the band',
      reduceBandCta: 'Answer key questions',
      reduceBy: (amount: string) => `To narrow the band by ~${amount}:`,
      respondQuestions: (count: number) =>
        `Answer ${count} question${count !== 1 ? 's' : ''} →`,
      expandBucket: 'Expand bucket',
      collapseBucket: 'Collapse bucket',
      bucketSourcesLabel: 'Sources used',
      bucketSubItems: 'Estimated sub-items',
      bucketSources: 'Sources',
      bucketRefineHints: 'To refine',
      bandReductionTitle: 'Tighten the band',
      bandCurrent: (band: string, pct: string) => `Current band: ± ${band} (${pct})`,
      bandMinPossible: (band: string, pct: string) =>
        `Minimum possible band: ± ${band} (${pct})`,
      answeredOfTotal: (answered: number, total: number) =>
        `You answered ${answered} of ${total} questions`,
      answerMoreCta: 'Answer more questions to tighten the band →',
    },
    questions: {
      estimatedReduction: (amount: string) => `🎯 Reduces ± ${amount}`,
      estimatedReductionTooltip: (amount: string) =>
        `Answering this question reduces the uncertainty interval by ± ${amount}`,
      contextualHeader: 'Questions to tighten the band',
    },
    omissions: {
      title: (count: number) =>
        count === 1 ? 'Thing your estimate does NOT include (1)' : `Things your estimate does NOT include (${count})`,
      subtitle: 'Categories every BR residential build should cover',
      hiddenCost: (range: string) => `Estimated hidden cost: ${range}`,
      respondNow: 'Answer now',
      addManual: 'Add manual value',
      allCovered: 'Estimate covers all canonical categories.',
      statusAmbiguoTooltip:
        'Assertion exists but value is zero or "none" — confirm there really is none.',
      priorityCritical: 'Critical',
      priorityHigh: 'Important',
      priorityMedium: 'Heads-up',
    },
    volatility: {
      title: 'Price volatility (INCC + materials)',
      expectedInflation: (months: number, pct: string) =>
        `Expected INCC inflation over ${months} months: ${pct}`,
      worstCase: (pct: string) => `Pessimistic scenario (p90): ${pct}`,
      hint:
        'Longer schedules = more exposure to steel/cement/labor inflation. Band widens accordingly.',
    },
    telemetry: {
      title: 'History for this estimate',
      summaryLine: (inferences: number, overrides: number) =>
        `${inferences} inference${inferences === 1 ? '' : 's'} · ${overrides} PM override${overrides === 1 ? '' : 's'}`,
      bandJourney: (fromPct: string, toPct: string) =>
        `Band went from ±${fromPct} → ±${toPct}`,
      sinceMinutes: (min: number) => `in ${min} min`,
      detailsCta: 'View details →',
      drawerTitle: 'Estimate history',
      historySection: 'Engine inferences',
      overridesSection: 'PM overrides',
      featuresSection: 'Signals used in the last inference',
      emptyHistory: 'No inferences yet.',
      overrideCount: (n: number) => `${n} override${n === 1 ? '' : 's'}`,
      closeCta: 'Close',
    },
    analogical: {
      title: (k: number) =>
        k === 0
          ? 'Similar builds in the database'
          : `Similar builds in the database (${k} found)`,
      emptyTitle: 'Model is still learning',
      emptyHint:
        'First build of this kind in the analogical store. Band stays wider until we have more similar history.',
      avgCostPerM2: (cost: string) => `Avg. cost ${cost}/m²`,
      yourEstimate: (cost: string) => `Your estimate: ${cost}/m²`,
      confBaixa: 'Low confidence',
      confMedia: 'Medium confidence',
      confAlta: 'High confidence',
      detailsCta: 'View neighbors →',
      drawerTitle: 'Analogical neighbors',
      statusProjected: 'Projected',
      statusInProgress: 'In progress',
      statusClosedActual: 'Closed',
    },
    actuals: {
      title: 'Your experience (Planned vs Actual)',
      avgVariancePrefix: (n: number) =>
        `Your last ${n} project${n === 1 ? '' : 's'} missed on average:`,
      avgVarianceSuffix: (pct: number) =>
        pct >= 0
          ? `+${pct.toFixed(0)}% over (you underestimate)`
          : `${pct.toFixed(0)}% under (you overestimate)`,
      worstLabel: 'Worst bucket:',
      bestLabel: 'Best bucket:',
      recommendationLabel: 'Recommendation for this project:',
      recommendationBody: (bucketLabel: string, pct: number) =>
        `Consider the upper band on the ${bucketLabel} bucket (+${pct.toFixed(0)}% historical).`,
      historyLabel: 'Your past projects:',
      detailsCta: 'View details',
      emptyTitle: '🌱 Model is still learning',
      emptyHint:
        'When you close your first projects, the system will show here how much you typically miss and in which buckets — to adjust future estimates.',
      drawerTitle: 'Planned vs Actual by bucket',
      colBucket: 'Bucket',
      colPlanned: 'Planned',
      colActual: 'Actual',
      colVariance: 'Variance',
      bucketEstrutura: 'Structure',
      bucketAlvenaria: 'Masonry',
      bucketAcabamento: 'Finishes',
      bucketInstalacoes: 'Installations',
      bucketExternos: 'External',
      bucketBdiCanteiro: 'BDI + site',
    },
  },

  // Wave G · Language cleanup — human-friendly EN labels for technical terms.
  glossary: {
    selfHeal: 'automatic correction',
    drift: 'unexpected change',
    anomaly: 'attention point',
    anomalies: 'attention points',
    proposal: 'suggestion',
    proposals: 'suggestions',
    trace: 'technical log',
    tier: 'level',
    payload: 'data',
    guardrail: 'protection rule',
    guardrails: 'protection rules',
    kindSelfHealFailed: 'Automatic correction failed',
    kindAnomalyHigh: 'Attention point',
    kindAnomalyResolve: 'Attention point',
    nudgeAnomalyOpen: 'Open attention point',
    tierLevel: 'level',
    tierLevelN: 'Level',
    tier1Label: 'automatic',
    tier2Label: 'supervised',
    tier3Label: 'requires your approval',
    tier4Label: 'your decision',
    originSystem: 'Builder System (automatic)',
    originPrumo: 'Site assistant · automatic rule',
    auditTitle: 'Audit',
    auditWho: 'Who decided',
    auditWhen: 'When',
    auditWhy: 'Why',
    auditImpact: 'Impact',
    auditOrigin: 'Origin',
    auditRawJson: 'View raw data (admin)',
    techDetailsAdmin: 'Technical details (admin)',
    prumoScanDaily: 'The site assistant scans projects every morning and puts cards here when something needs your OK.',
  },
  // Wave M · Admin Trena Sanity Ranges
  adminTrenaSanityRanges: {
    title: 'Trena Sanity Ranges',
    subtitle: 'Configure acceptable consumption ranges per material. Tenant overrides replace system defaults.',
    systemDefault: 'System default',
    tenantOverride: 'Tenant override',
    overrideCount: (n: number) => `${n} active override${n !== 1 ? 's' : ''}`,
    colComposition: 'Material',
    colUnit: 'Base unit',
    colSystemRange: 'System range',
    colTenantRange: 'Tenant range',
    colStatus: 'Status',
    statusOverridden: 'Customized',
    statusDefault: 'Default',
    addOverride: 'Add override',
    editOverride: 'Edit',
    deleteOverride: 'Remove',
    confirmDelete: 'Remove this override and revert to system default?',
    rangeMin: 'Minimum',
    rangeMax: 'Maximum',
    rangeLabel: 'Label (optional, e.g. un/m²)',
    saveOverride: 'Save',
    saving: 'Saving...',
    overrideSaved: 'Override saved successfully.',
    overrideDeleted: 'Override removed. Using system default.',
    emptyTitle: 'No custom ranges',
    emptyDesc: 'All ranges use system defaults. Add an override to adjust for this tenant.',
    feedbackBadgeTitle: 'Based on real measurements',
    feedbackBadgeSuffix: 'real measurements.',
    feedbackSuggest: (min: number, max: number) => `Suggested adjustment: ${min}–${max}`,
    applyFeedbackRanges: 'Apply feedback suggestions',
    applyingRanges: 'Applying...',
    rangesApplied: (n: number) => `${n} range${n !== 1 ? 's' : ''} adjusted based on real data.`,
    noFeedbackData: 'No real measurement data yet.',
  },
  // DP1 · NextActionCard namespace
  nextAction: {
    loading: 'Loading next action…',
    projectsLateRdoSingle: (name: string) => `${name} has an overdue RDO today`,
    projectsLateRdoMultiple: (count: number) => `${count} projects with overdue RDO today`,
    projectsLateImpact: (total: number) =>
      `${total} ${total === 1 ? 'log' : 'logs'} overdue — client may request update`,
    projectsLateCtaLabel: 'Open project',
    overviewEmptyTitle: 'Project created — start with the budget',
    overviewEmptyImpact: 'Trena extracts quantities from the floor plan PDF in minutes',
    overviewEmptyCta: 'Start with Trena',
    budgetPendingVersions: (count: number) =>
      `${count} ${count === 1 ? 'version' : 'versions'} awaiting approval`,
    budgetPendingImpact: 'Approval releases budget commitment for purchases',
    budgetPendingCta: 'Approve pending version',
    budgetHighUsage: (category: string, pct: number) =>
      `${category} budget at ${pct}% used`,
    budgetHighUsageImpact: 'Risk of exceeding category before project end',
    budgetHighUsageCta: 'Review items',
    budgetNoSinapi: 'Budget without reference pricing',
    budgetNoSinapiImpact: 'Linking reference prices (SINAPI) improves traceability',
    budgetNoSinapiCta: 'Review references',
    purchasesWaiting: (count: number, days: number) =>
      `${count} ${count === 1 ? 'quotation awaits' : 'quotations await'} response for ${days}+ days`,
    purchasesWaitingImpact: 'Supplier may cancel proposal — deadline approaching',
    purchasesWaitingCta: 'Send follow-up',
    qualidadeOverdueSingle: (projects: number) =>
      `1 overdue inspection in ${projects} ${projects === 1 ? 'project' : 'projects'}`,
    qualidadeOverdueMultiple: (count: number, projects: number) =>
      `${count} overdue inspections in ${projects} ${projects === 1 ? 'project' : 'projects'}`,
    qualidadeImpact: 'Overdue inspections block the stage and increase rework risk',
    qualidadeCta: 'Open inspections list',
    financialHighUsage: (category: string, pct: number) =>
      `${category} budget at ${pct}% used`,
    financialHighUsageImpact: 'Risk of exceeding budget before project end',
    financialHighUsageCta: 'Review',
    scheduleMilestone: (name: string, daysLabel: string) =>
      `"${name}" ${daysLabel}`,
    scheduleImpact: 'Confirm progress to keep the schedule up to date',
    scheduleCta: 'Confirm progress',
    photosUnlinked: (count: number) =>
      `${count} ${count === 1 ? 'photo without linked RDO' : 'photos without linked RDO'}`,
    photosUnlinkedImpact: 'Unlinked photos do not appear in client reports',
    photosUnlinkedCta: 'Link to RDO',
    trenaDraftOld: (days: number) => `Estimate open for ${days} days without commit`,
    trenaDraftImpact: 'Open estimates do not feed the project budget',
    trenaDraftCta: 'Continue estimate',
    inboxPending: (count: number) =>
      `${count} pending ${count === 1 ? 'decision' : 'decisions'}`,
    inboxImpact: 'Pending decisions may block purchases and payments',
    inboxCta: 'Review next',
  },

  dp5: {
    inlineSearch: {
      placeholder: 'Search…',
      clearAriaLabel: 'Clear search',
      countLabel: (filtered: number, total: number) => `${filtered} of ${total}`,
    },
    keyboardShortcuts: {
      drawerTitle: 'Shortcuts',
      groupSearch: 'Search',
      groupNav: 'Navigation',
      groupGeneral: 'General',
      globalSearch: 'Global search',
      listSearch: 'List search',
      goHome: 'Go to Today',
      goProjects: 'Go to Projects',
      goInbox: 'Go to Inbox',
      goNotifications: 'Go to Notifications',
      showShortcuts: 'Show shortcuts',
      closeOverlay: 'Close overlay',
      mobileNote: 'Shortcuts work on desktop. On mobile use the bottom bar.',
      then: 'then',
    },
    autocomplete: {
      noResults: 'No results',
      create: (text: string) => `Create "${text}"`,
      clearAriaLabel: 'Clear selection',
    },
    recentItems: {
      sectionTitle: 'Recently viewed',
    },
  },
  pageHeader: {
    more: 'More',
    moreActionsAriaLabel: 'More actions',
  },
  welcome: {
    greeting: (firstName) => `Welcome to Builder, ${firstName}`,
    subtitle:
      'Construction project management that fits in your pocket: budget, daily logs, ' +
      'purchases, and billings — all in one place. How would you like to start?',
    demoTitle: 'Explore with a sample project',
    demoDescription:
      'A fictitious 120m² remodel pre-filled with everything. Edit, delete, play around — ' +
      'no real project is affected.',
    demoCta: 'View sample project',
    demoInventory: '15 daily logs · 12 purchase orders · 50 budget items · 2 quality issues · 3 technical questions',
    demoEstimatedTime: '5 minutes to explore',
    createTitle: 'Create a real project',
    createDescription:
      'Start a brand-new project. In one minute you register client, address, and project type — ' +
      "Builder will then suggest what to do next.",
    createCta: 'Create project',
    importTitle: 'Import from Excel',
    importDescription:
      "Already have a budget in Excel? Import it directly: Builder reads your sheet in 30 seconds, " +
      'shows the diff preview, you confirm or undo.',
    importCta: 'Import spreadsheet',
    skip: 'Skip for now',
    demoLoading: 'Preparing sample project…',
    demoError: "Couldn't prepare the sample project right now. Try again in a few seconds.",
    demoRetry: 'Try again',
  },
  // W123.4 · 2026-05-26 · Next action (replaces "Prumo" brand).
  proximaAcao: {
    title: 'Next action',
  },
  tour: {
    skip: 'Skip tour',
    next: 'Next',
    back: 'Back',
    finish: 'Done, let us start',
    counter: (current, total) => `Step ${current} of ${total}`,
    retake: 'Retake tour',
    startCta: 'Take a 2-minute Builder tour',
    steps: {
      welcome: {
        title: 'Welcome to Builder',
        body: "I will show you in 2 minutes how everything connects. You can skip anytime.",
      },
      search: {
        title: 'Global search · Cmd+K',
        body: 'Find any project, RDO, order or supplier. Works on every screen.',
      },
      chat: {
        title: 'Quick chat',
        body: 'Speech bubble for instant messaging with the crew. 7-day history, emoji, presence.',
      },
      inbox: {
        title: 'Formal inbox',
        body: 'Envelope for messages with subject and audit. Everything is logged for traceability.',
      },
      hoje: {
        title: 'Today · your next actions',
        body: 'Day cockpit: the 3 most important things to handle right now, across projects.',
      },
      trena: {
        title: 'Trena · smart estimating',
        body: 'Import photo or PDF, AI extracts measurements, Builder prices via the official reference table (SINAPI).',
      },
      comando: {
        title: 'Command · admin view',
        body: 'Kanban with every project in your portfolio. Filter by urgency, drag-and-drop.',
      },
      done: {
        title: 'Done. Time to create your first project.',
        body: 'The rest you learn by using. If you get stuck, open the Help center or retake the tour.',
      },
    },
  },
  status: {
    title: 'Builder status',
    subtitle: 'Live platform status. Refreshes every 30 seconds.',
    pillOk: 'All systems operational',
    pillDegraded: 'Partial outage',
    pillDown: 'Unavailable',
    cardDb: 'Database',
    cardR2: 'Object storage (photos)',
    cardCron: 'Scheduled jobs',
    latency: (ms) => `${ms} ms`,
    lastCronAt: (when) => `Last run: ${when}`,
    lastCronNever: 'No runs recorded yet',
    historyTitle: 'Last 24 hours',
    historyEmpty: 'No history yet — the monitor is still collecting data.',
    loading: 'Loading status…',
    errorTitle: 'Could not load status',
    errorBody: "We'll retry automatically in a few seconds.",
    updatedAt: (when) => `Updated at ${when}`,
  },
  firstRun: {
    cancel: 'Cancel',
    autoSaving: 'auto-saving',
    savedAt: (time) => `saved at ${time}`,
    resumeTitle: 'You have unsaved progress',
    resumeBody: (ago) => `Started ${ago}. Continue or start over?`,
    resumeContinue: 'Continue',
    resumeStartFresh: 'Start fresh',
    next: 'Next',
    back: 'Back',
    complete: 'Complete',
    stepOf: (current, total) => `${current} of ${total}`,
    defaultBlockMsg: 'Fill in the required fields to continue.',
  },
  chat: {
    sendPlaceholder: 'Message…',
    sendHint: 'Enter to send · Shift+Enter for new line',
    noMessages: 'No messages yet.',
    online: 'Online now',
    minimize: 'Minimize',
    close: 'Close',
    openChat: (name) => `Send message to ${name}`,
    closeChat: (name) => `Close chat with ${name}`,
  },
  moduleProgress: {
    stepsReady: (done, total) => `${done} of ${total} steps ready`,
    progressBarLabel: (module) => `${module} progress`,
    lockedPrefix: 'Locked:',
    rdo: {
      moduleLabel: 'Daily Log',
      completedLabel: 'Daily log set up — your client will love it.',
    },
    fvs: {
      moduleLabel: 'Quality Inspection',
      completedLabel: 'Quality on track — keep inspecting.',
    },
    orcamento: {
      moduleLabel: 'Budget',
      completedLabel: 'All set to start purchasing.',
    },
    verba: {
      moduleLabel: 'Budget Envelopes',
      completedLabel: 'Envelopes configured — track your spending.',
    },
    compras: {
      moduleLabel: 'Purchases',
      completedLabel: 'First purchase cycle complete.',
    },
    pranchas: {
      moduleLabel: 'Drawings',
      completedLabel: 'Project imported and linked to RFIs.',
    },
    trena: {
      moduleLabel: 'Trena',
      completedLabel: 'Estimate approved and in budget.',
    },
  },
  adminJargon: {
    storageTitle: 'Storage',
    storageDescription: 'Document storage statistics.',
    organization: 'Organization',
    organizations: 'Organizations',
    organizationSwitcherTitle: 'Organization Switcher',
    organizationSwitcherSubtitle: 'List available organizations and switch context.',
    organizationListHeading: (n) => `Organizations (${n})`,
    organizationsTabLabel: 'Organizations',
    organizationsTabDescription: 'Platform organizations and contexts.',
    organizationsTabHelp:
      'This tab shows platform organizations. Use it to switch context between organizations or verify project counts.',
    organizationsEmptyTitle: 'No organizations',
    organizationsEmptyDescription: 'No organizations found in the platform.',
    organizationProjectCount: (n) => `${n} project${n !== 1 ? 's' : ''}`,
    organizationSwitch: 'Switch',
    organizationSwitching: 'Switching…',
    selfHealing: 'Auto-recovery',
    selfHealingT1: 'Auto-recovery T1',
    traceTabLabel: 'Technical history',
    bulkOpsDescription: 'Bulk operations (remove, reassign, etc.).',
    driftBadge: 'unexpected change',
    driftBadgeTitle:
      'One or more keys in this composition are not in the catalog — possible unexpected change between rule and compatibility.',
    driftKeyTooltip: (key) =>
      `Key "${key}" is not in the catalog — possible unexpected change`,
  },
  obraType: {
    residencial_popular: 'House (up to 80m²)',
    residencial_medio: 'Larger house (80-200m²)',
    comercial_loja: 'Store or shop (up to 500m²)',
    industrial_galpao: 'Warehouse or industrial',
    recommendedForYourType: 'Recommended for your project type',
    otherTemplates: 'Other templates',
    label: 'Project type',
    optional: 'Project type (optional)',
    helperText: 'Helps Trena suggest the right budgets for your project type.',
    placeholder: 'Select…',
  },
  region: {
    label: 'Project state',
    optional: 'Project state (optional)',
    helperText:
      'Trena uses the reference price (SINAPI) for the selected state; falls back to national when local data is missing.',
    national: 'National (CAIXA · default)',
    pilotGroup: 'Pilot states',
    otherGroup: 'Other states',
  },
  sinapiSource: {
    ufExact: 'Official state price',
    derived: 'Estimated regional price',
    national: 'National price',
    derivedHint:
      'Estimated from national price × state factor. For exact precision, import the CAIXA SINAPI worksheet for your state.',
  },

  trenaExport: {
    exportCsv: 'Export Excel/CSV',
    exportCsvHint: 'Universal format — opens in Excel or any ERP.',
    exportCsvTooltip:
      'Opens in Excel or any construction ERP (SISCONTROLE, SIENGE, Sage, etc).',
  },

  chatInstant: {
    launcher: {
      tooltip: 'Conversations',
      unreadSuffix: (n) => `${n} unread`,
    },
    panel: {
      title: 'Conversations',
      subtitle: 'Quick messages. Disappear after 7 days.',
      searchPlaceholder: 'Search person or group',
      newButton: 'New',
      newSearchPlaceholder: 'Type a name',
      newNoResults: 'Nobody found.',
      tabs: {
        all: 'All',
        unread: 'Unread',
        groups: 'Groups',
      },
      empty: 'No conversations yet.',
      emptyUnread: 'All caught up.',
      emptySearch: 'Nothing matches that search.',
      emptyCta: 'Start a new conversation',
    },
    window: {
      placeholder: 'Type a message...',
      send: 'Send',
      typing: (name) => `${name} is typing...`,
      online: 'online',
      offline: (when) => `last seen ${when}`,
      empty: 'Send the first message.',
      openEmojis: 'Open emojis',
    },
    dock: {
      newCount: (n) => (n === 1 ? '1 new' : `${n} new`),
      moreSenders: (n) => `+ ${n} more`,
      dismiss: 'Dismiss',
      openWith: (name) => `Open chat with ${name}`,
    },
    pills: {
      label: 'Recent conversations',
      moreCount: (n) => `+${n} more`,
      dismissPin: 'Close this conversation pin',
      openWith: (name) => `Open chat with ${name}`,
      openGeneric: 'Open chat',
    },
  },
  maps: {
    addressPlaceholder: 'Street, number, neighborhood, city…',
    loadingSdk: 'Loading address suggestions',
    autocompleteUnavailable:
      'Suggestions offline — type the address manually and we geocode it server-side.',
    loadingThumbnail: 'Loading map',
    thumbnailUnavailable: 'Map unavailable',
    staticMapAlt: 'Address map',
    interactiveMapAria: 'Interactive address map',
    loadingMap: 'Loading map…',
    refreshGeo: 'Refresh geo for this address',
    refreshGeoQueued: 'New geocoding requested. Refresh the page in a few seconds.',
    distanceBadge: (km, min) => `${km.toFixed(1)} km · ${min} min`,
    geoCaptured: 'Location captured from autocomplete',
  },
  weather: {
    chipAria: (description, tempC) => `Weather: ${description}, ${Math.round(tempC)}°C`,
    chipSummaryDry: 'next 3d dry',
    chipSummaryRain: (label) => `rain ${label}`,
    expand: 'Show 7-day forecast',
    collapse: 'Hide forecast',
    forecastTitle: '7-day forecast',
    unavailable: 'Weather unavailable right now',
    missingGeo: 'Add the project address to see the weather',
    rainWarning: 'Heavy rain expected — consider rescheduling outdoor work',
    externalStageWarning: 'Outdoor stage on a heavy-rain day',
    dayShort: ['Today', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    todayLabel: 'Today',
    conditionClear: 'Clear',
    conditionCloudy: 'Partly cloudy',
    conditionRainLight: 'Light rain',
    conditionRainHeavy: 'Heavy rain',
    conditionStorm: 'Storm',
    conditionSnow: 'Snow',
    conditionFog: 'Fog',
    conditionUnknown: 'Unavailable',
    rdoAutoFilled: 'auto-filled',
    rdoAutoFillFailed: "Couldn't fetch the weather — fill in manually",
  },
  adminLabsGenerative: {
    title: 'Labs · Generative Engineering',
    subtitle: 'Describe your project and AI generates a sketch: floor plan, specs, budget, schedule, and RFQs.',
    sketchBadge: 'Sketch — requires technical review',
    disclaimer: '💡 This is an AI-generated sketch. It does not replace a project by an architect/engineer registered with CREA/CAU.',
    promptPlaceholder: 'Describe your project. Example: Single-story house 120m² 3 bedrooms in Sorocaba, mid-range, 8 months, flat lot',
    generateBtn: 'Generate sketch',
    generating: 'Generating…',
    tabs: {
      planta: 'Floor Plan',
      memorial: 'Specs',
      orcamento: 'Budget',
      cronograma: 'Schedule',
      cotacoes: 'RFQs',
    },
    steps: {
      planta: '✏️ Sketching floor plan…',
      memorial: '📝 Writing specs…',
      orcamento: '💰 Estimating budget…',
      cronograma: '📅 Building schedule…',
      cotacoes: '📦 Listing RFQs…',
    },
    orcamento: {
      total: 'Estimated total',
      area: 'Area',
      padrao: 'Standard',
      tipologia: 'Type',
      buckets: 'Cost breakdown',
      categoria: 'Category',
      percentual: '%',
      valor: 'Estimated value',
    },
    cronograma: {
      duration: 'Total duration',
      months: 'months',
      activity: 'Activity',
      month: 'Month',
      days: 'Days',
    },
    cotacoes: {
      item: 'Item',
      qtd: 'Qty',
      unit: 'Unit',
    },
    emptyTitle: 'Ready to generate',
    emptyHint: 'Describe your project above and click "Generate sketch".',
    errorTitle: 'Error generating sketch',
    plantaUnavailable: 'Floor plan not generated in this sketch.',
    v2: {
      tabBrief: 'Brief',
      tabLayout: 'Layout',
      tabEstimate: 'Estimate & Specs',
      briefHeader: 'What the system extracted from your prompt',
      briefTipologia: 'Type',
      briefArea: 'Built area',
      briefPadrao: 'Standard',
      briefRegiao: 'Region',
      briefPrazo: 'Estimated schedule',
      briefSistemas: 'Special systems',
      briefComodos: 'Rooms identified',
      briefDescricao: 'Architectural description',
      briefPremissas: 'Stated assumptions',
      briefLacunas: 'Gaps (LLM had to guess)',
      briefTermos: 'Prompt terms (cited verbatim in specs)',
      briefMonths: 'months',
      layoutHeader: 'Suggested rooms (declared heuristic)',
      layoutAreaTotal: 'Sum of areas',
      layoutAreaDeclarada: 'Brief declared area',
      layoutAreaWarning: 'Discrepancy > 10% — review area in Brief.',
      estimateHeader: 'Estimate (real engine)',
      estimateBandLabel: '90% CI band',
      estimateCubLabel: 'Comparison with CUB SINDUSCON',
      estimateCubPadrao: 'CUB standard',
      estimateYourPrice: 'Your R$/m²',
      estimateDiff: 'Difference',
      estimateBuckets: 'Composition (from confidence engine)',
      estimateMemorial: 'Specs (memorial)',
      noneDash: '—',
      briefEditTipologiaLabel: 'Type',
      briefEditPadraoLabel: 'Standard',
      briefEditAreaLabel: 'Area (m²)',
      briefEditPrazoLabel: 'Schedule (months)',
      briefEditUfLabel: 'State',
      briefEditCidadeLabel: 'City',
      briefEditSistemasLabel: 'Special systems',
      briefEditSistemasAddPlaceholder: 'Add system…',
      briefEditSistemasAdd: 'Add',
      recalcBtn: 'Recalculate estimate',
      recalcLoading: 'Recalculating…',
      recalcDone: 'Estimate updated',
      layoutSvgFooter: 'Schematic sketch — not an architectural floor plan',
    },
  },
  adminLabsGenerativeHistory: {
    pageTitle: 'Generative Lab History',
    searchPlaceholder: 'Search by prompt…',
    columnPrompt: 'Prompt',
    columnPadrao: 'Standard',
    columnEstimativa: 'Estimate',
    columnStatus: 'Status',
    columnData: 'Date',
    columnTipologia: 'Type',
    columnArea: 'Area',
    statusConverted: 'Converted',
    statusPreview: 'Preview',
    emptyState: 'No V2 drafts yet. Generate one at /admin/labs/generative.',
    detailTitle: 'Draft Detail',
    detailClose: 'Close',
    detailBrief: 'Extracted brief',
    detailEstimate: 'Estimate',
    detailMemorial: 'Specs',
    loadError: 'Could not load history.',
  },
  adminMetaTemplates: {
    title: 'Meta WhatsApp Templates',
    subtitle: 'Approval status of UTILITY templates in Meta Business Manager.',
    refreshBtn: 'Refresh now',
    refreshing: 'Refreshing…',
    refreshSuccess: (checked: number) =>
      `${checked} template${checked !== 1 ? 's' : ''} checked.`,
    refreshErrors: (count: number) =>
      `${count} error${count !== 1 ? 's' : ''} during refresh — check the console.`,
    empty: 'No templates found.',
    emptyHint: 'Click "Refresh now" to fetch templates from Meta.',
    colName: 'Template',
    colStatus: 'Status',
    colCategory: 'Category',
    colLanguage: 'Language',
    colQuality: 'Quality',
    colLastChecked: 'Last checked',
    statusLabels: {
      APPROVED: 'Approved',
      PENDING: 'Pending',
      REJECTED: 'Rejected',
      DISABLED: 'Disabled',
      PAUSED: 'Paused',
      unknown: 'Unknown',
    },
    qualityLabels: {
      GREEN: 'Green',
      YELLOW: 'Yellow',
      RED: 'Red',
      UNKNOWN: 'Unknown',
    },
    rejectionLabel: 'Reason:',
    loadError: 'Could not load templates.',
  },
  // Wave 51 · 2026-05-23 · Procurement Autopilot dummy-proof v2.
  procurementAutopilot: {
    title: 'Procurement Autopilot',
    subtitle: 'Detects gaps, recommends actions and — when an auto-rule is active — closes purchases and issues orders automatically.',
    tabAlerts: (n: number) => `Detected alerts (${n})`,
    tabRules: (n: number) => `Auto rules (${n})`,
    tabExecutions: (n: number) => `Executions (${n})`,
    pauseIdleLabel: 'No active auto rules',
    pauseActiveLabel: (n: number) => `Pause autopilot (${n} active)`,
    pausingLabel: 'Pausing…',
    pauseConfirmTitle: 'Pause procurement autopilot?',
    pauseConfirmMessage: (n: number) => `Will deactivate all ${n} active auto-rule${n > 1 ? 's' : ''}. No automatic purchases will run until you re-enable manually.`,
    pauseConfirmCta: (n: number) => `Pause ${n} rule${n > 1 ? 's' : ''}`,
    approveConfirmTitle: 'Confirm review?',
    approveConfirmCta: 'Yes, confirm',
    declineConfirmTitle: 'Confirm decline?',
    declineConfirmCta: 'Yes, dismiss alert',
    createRfqBtn: 'Create quote',
    creatingRfqBtn: 'Creating quote…',
    revertPoBtn: 'Revert purchase',
    emptyRulesTitle: 'No auto rules configured',
    emptyRulesDesc: 'Create the first rule to enable automatic purchases by category.',
    newRuleBtn: 'New auto rule',
    saveRuleBtn: 'Save rule',
    editRuleBtn: 'Edit auto rule',
  },

  // W123.12 · 2026-05-26 · Project handover (guided closeout)
  entrega: {
    title: 'Project handover',
    activeSubtitle:
      'The project is in progress. When you are ready to hand it over, start the guided process — you will review pending items, sign the acceptance, and the Owner Manual is generated automatically.',
    activeCardTitle: 'Ready to hand over?',
    activeCardDesc:
      'Starting handover freezes daily logs, purchases and payments. You can still step back before marking it delivered.',
    startCta: 'Start handover',
    startingCta: 'Starting…',
    startError: 'Could not start handover. Please try again.',
    handoffTitle: 'Handover in progress',
    handoffSubtitle:
      'Resolve the items below before delivering the project to the owner. Red items block the handover.',
    completedTitle: 'Project delivered',
    completedOn: (date) => `Delivered on ${date}`,
    completedNoDate: 'Status: delivered',
    manualTitle: 'Owner Manual',
    manualDesc:
      'Complete manual with floor plans, warranties, contacts and maintenance instructions — ready to hand over to the owner.',
    manualDownload: 'Download Manual (PDF)',
    reopenTitle: 'Reopen project',
    reopenDesc:
      'Admin action. Use only if the project was marked delivered by mistake or if serious issues are discovered afterwards.',
    reopenCta: 'Reopen project (admin)',
    reopenConfirmTitle: 'Reopen delivered project?',
    reopenConfirmDesc:
      'The project returns to "In progress". All modules become editable again. This action is logged.',
    completeCta: 'Hand over to owner',
    completingCta: 'Handing over…',
    completeError: 'Failed to complete handover. Make sure the checklist is fully green.',
    confirmCompleteTitle: 'Hand over to owner?',
    confirmCompleteDesc:
      'The project will be marked delivered. The Owner Manual will be generated and available for download. This action is irreversible by the PM.',
    allClear: 'All clear. You can hand over to the owner.',
    blockedMsg: 'Resolve red items to unlock the handover.',
    item: {
      rnc: 'Open NCRs',
      rncOk: 'No open NCRs',
      rncOpen: (n) => `${n} open NCR${n === 1 ? '' : 's'}`,
      fvs: 'Failed inspections',
      fvsOk: 'No pending failed inspections',
      fvsFailed: (n) => `${n} inspection${n === 1 ? '' : 's'} with unverified non-conformity`,
      payments: 'Outstanding payments',
      paymentsOk: 'Fully paid',
      paymentsUnpaid: (amount) => `${amount} unpaid`,
      stock: 'Remaining stock',
      stockOk: 'No items in stock',
      stockRemaining: (n) => `${n} item${n === 1 ? '' : 's'} in stock (info)`,
      docs: 'Final documents',
      docsOk: 'Contract, ART, Permit and License attached',
      docsMissing: (list) => `Missing: ${list}`,
      photos: 'Final photos',
      photosCount: (n) => `${n} photo${n === 1 ? '' : 's'} (info)`,
      term: 'Acceptance term',
      termSigned: (date) => `Signed by owner on ${date}`,
      termUnsigned: 'Acceptance term not yet signed by owner',
      collectSignature: 'Collect signature',
      cancel: 'Cancel',
      ownerNamePlaceholder: 'Owner name',
      confirmSign: 'Confirm',
      signing: 'Signing…',
      signError: 'Could not save signature. Please try again.',
      seeRnc: 'View non-conformances',
      seeFvs: 'View inspections',
      goMoney: 'Go to Money',
      attachDocs: 'Attach documents',
    },
  },
};
